/**
 * @fileoverview Главный файл legacy приложения
 * 
 * Этот файл является точкой входа для legacy приложения Glitchlings.
 * Он инициализирует все менеджеры, настраивает глобальные функции и API,
 * и управляет жизненным циклом приложения.
 * 
 * @module legacy-app
 */

import { showTelegramOverlay, hideTelegramOverlay } from "./legacy/overlay.js";
import { safeFetch as baseSafeFetch, resolveApiUrl, setAccessDeniedChecker } from "./legacy/api.js";
import { createProfessionsService } from "./legacy/professions.js";
import { createAchievementNotificationManager } from "./legacy/notifications/achievementNotifications.js";
import { createTelegramAuthManager } from "./legacy/init/telegramAuthManager.js";
import { createTelegramBootstrapManager } from "./legacy/init/telegramBootstrapManager.js";
import { createLegacyRouter } from "./legacy/navigation/router.js";
import { updateUserBalancesUI, createHomeScreenLoader } from "./legacy/utils/userData.js";
import { createErrorbornStatusManager } from "./legacy/screens/errorbornStatus.js";
import { createStoreScreenLoader } from "./legacy/screens/store.js";
import { createCollectionScreenManager } from "./legacy/screens/collection.js";
import { createGiftsScreenManager } from "./legacy/screens/gifts.js";
import { renderAdminScreen, isCurrentUserAdmin, setBuildAuthHeaders, checkAdminStatus } from "./legacy/screens/admin.js";
import { createPaymentScreenManager } from "./legacy/screens/payment.js";
import { createProfessionsUpgradesManager } from "./legacy/screens/professions.js";
import { createBattleTelemetryManager } from "./legacy/battle/telemetry.js";
import { escapeHtml, formatStatName } from "./legacy/utils/dom.js";
import { createCreatureCardsRenderer } from "./legacy/ui/creatureCards.js";
import { createErrorbornQuestRenderer } from "./legacy/ui/errorbornQuest.js";
import { createAdminButtonManager } from "./legacy/ui/adminButton.js";
import { setupGlobalErrorHandlers, createSafeFetchWrapper } from "./legacy/utils/errorHandler.js";
import { normalizeString, findCreatureInCollection, createCollectionDataEnsurer } from "./legacy/utils/helpers.js";
import { getTelegramWebAppSafe, setTelegramWebAppSafe, initTelegramWebApp, hasTelegramWebApp, hasTelegramUserAgent, isInTelegram } from "./legacy/utils/telegram.js";
import { createLoadingIndicatorManager } from "./legacy/utils/loadingIndicator.js";
import { createDOMCacheManager } from "./legacy/utils/domCache.js";
import { createUserMessageManager } from "./legacy/utils/userMessages.js";
import { setLoadingIndicatorManager, setUserMessageManager } from "./legacy/api.js";
import { logger } from "./utils/logger.js";
import { applyRandomBackgroundToHome, removeBackground } from "./legacy/utils/backgroundManager.js";
import { DEFAULT_CREATURE_STATS, STAT_LIMITS } from "./legacy/constants/battleDefaults.js";
import { createInitialBattleState, createInitialPvpState } from "./legacy/state/battleState.js";
import { 
  STATUS_EFFECTS as B, 
  parseStatusEffect as Kt,
  createStatusEffect as R,
  normalizeStatusEffect as Zn,
  cleanupExpiredStatusEffects as ss,
  serializeStatusEffects as os,
  addStatusEffect as ee,
  syncStatusEffects as ns,
  processBattleStatusEffects as is,
  processEventStatusEffects as as,
  getCombatantName as te,
  formatStatusName as Yt,
  formatPercent as K
} from "./legacy/battle/statusEffects.js";
import { createAchievementsManager } from "./legacy/screens/achievements.js";
import { createDailyLoginManager } from "./legacy/screens/dailyLogin.js";
import { createTransactionsManager } from "./legacy/screens/transactions.js";
import { createCreaturePurchaseManager } from "./legacy/utils/creaturePurchase.js";
import { createCreatureDetailsManager, normalizeStats } from "./legacy/ui/creatureDetails.js";
import { createBattleModalManager } from "./legacy/battle/modal.js";
import { createBattleAnimationsManager } from "./legacy/battle/animations.js";
import { createBattleEnhancementsManager } from "./legacy/battle/battleEnhancements.js";
import { initMobileOptimizations } from "./legacy/battle/mobileOptimizations.js";
import { createBattleHistoryManager } from "./legacy/screens/battleHistory.js";
import { createBattleControlsManager } from "./legacy/battle/controls.js";
import { createFormationManager } from "./legacy/battle/formation.js";
import { buildBattlePanels, determineWinnerInfo, getPanelById } from "./legacy/battle/panels.js";
import { buildBattleTimeline } from "./legacy/battle/timeline.js";
import { renderBattlePlaybackPanels, renderBattlePlaybackLog, renderBattlePlaybackStage } from "./legacy/battle/render.js";
import { 
  renderBattlePlaybackPanels as renderPanelsOptimized,
  renderBattlePlaybackLog as renderLogOptimized,
  renderBattlePlaybackStage as renderStageOptimized,
  clearBattleRenderCache
} from "./legacy/battle/renderOptimized.js";
import {
  renderBattlePanels,
  renderBattleStage,
  renderBattleProgress,
  renderBattleLog
} from "./legacy/battle/playback.js";
import { calculateBattleStats, formatBattleStats, renderBattleStats } from "./legacy/battle/stats.js";
import { createUserAutocompleteManager } from "./legacy/utils/userAutocomplete.js";
import { updateWeeklyRewards } from "./legacy/utils/formatters.js";
import { TYPE_NAMES, BATTLE_AI_TEMPLATES } from "./legacy/constants.js";
import { GLITCHLINGS_HEROES } from "./legacy/battle/glitchlingsBattle.js";
import { createMultiplayerBattleManager } from "./legacy/battle/multiplayerBattle.js";
import { createProgressPassManager } from "./legacy/screens/progressPass.js";
import { startMultiplayerBattle as startMultiplayerBattleFromModule } from "./legacy/battle/multiplayerBattleStarter.js";
import { activateAllFilter } from "./legacy/utils/collectionFilters.js";
import { initializeBattleHandlers } from "./legacy/utils/battleInitialization.js";
import { renderBattleResult } from "./legacy/battle/battleResultRenderer.js";
import { findAiPreset } from "./legacy/utils/aiPresetFinder.js";
import { createLootboxManager } from "./legacy/screens/errorBoxes.js";
import { createLootboxInventoryManager } from "./legacy/screens/lootboxInventory.js";
import { createEnergyManager } from "./legacy/screens/energyManager.js";
import { createExpManager } from "./legacy/screens/expManager.js";
import { createClubManager } from "./legacy/screens/clubManager.js";
import { createGiftCreatureCard, selectGiftCreature } from "./legacy/ui/giftCreatureCard.js";
import { createGiftItem } from "./legacy/ui/giftItem.js";
import { formatBattleLogEntry, extractBattleFlags } from "./legacy/battle/battleLogFormatter.js";
import { showBattleWinnerBanner, hideBattleWinnerBanner } from "./legacy/battle/battleWinnerBanner.js";
import { formatTelemetryEventName, formatTelemetryEvent, processTelemetryByRound } from "./legacy/battle/telemetryFormatter.js";
import { formatCreaturePreview, formatAiPresetPreview, formatRandomCreaturePreview, formatPreviewStatsHtml, renderCreaturePreview, getCreaturePreview } from "./legacy/battle/preview.js";
import { determineBattleWinner } from "./legacy/battle/winnerDetermination.js";
import { loadBattleData } from "./legacy/battle/battleLoader.js";
import { createPvpManager, parseUserCreatureId, getUserName } from "./legacy/pvp/pvpManager.js";
import { createBattlePreviewManager } from "./legacy/battle/battlePreviewManager.js";
import { createBattleViewManager } from "./legacy/battle/battleViewManager.js";
import { createBattleSimulationRunner } from "./legacy/battle/battleSimulationRunner.js";
import { createBattlePlaybackManager } from "./legacy/battle/battlePlaybackManager.js";
import { createTelemetryProcessor } from "./legacy/battle/telemetryProcessor.js";
import { createGiftHelpers } from "./legacy/ui/giftHelpers.js";

/**
 * Инициализирует legacy приложение
 * 
 * Основная функция инициализации приложения. Выполняет:
 * - Проверку доступа через whitelist
 * - Инициализацию Telegram WebApp
 * - Настройку глобальных обработчиков ошибок
 * - Инициализацию менеджеров (auth, bootstrap, router, screens)
 * - Настройку глобальных функций и API
 * 
 * @returns {Promise<void>}
 * @throws {Error} Если инициализация не удалась
 * 
 * @example
 * await initLegacyApp();
 */
export async function initLegacyApp() {
  // ПРОСТАЯ ПРОВЕРКА ДОСТУПА: проверяем whitelist ДО инициализации всего остального
  // fetch уже заблокирован в main.js
  try {
    const { checkUserAccess, showAccessDeniedStub } = await import('./legacy/utils/accessCheck.js');
    const apiUrl = resolveApiUrl();
    
    console.log('[initLegacyApp] 🔍 Проверка доступа пользователя...');
    console.log('[initLegacyApp] API URL:', apiUrl);
    logger.debug('[initLegacyApp] Has Telegram:', !!window.Telegram);
    logger.debug('[initLegacyApp] Has WebApp:', !!(window.Telegram && window.Telegram.WebApp));
    logger.debug('[initLegacyApp] Has initData:', !!(window.Telegram?.WebApp?.initData));
    
    // НЕ используем baseSafeFetch - он может использовать заблокированный fetch
    // Используем прямой вызов checkUserAccess без safeFetch
    const accessCheck = await checkUserAccess(null, apiUrl);
    
    console.log('[initLegacyApp] Access check result:', accessCheck);
    
    if (!accessCheck.hasAccess) {
      console.log('[initLegacyApp] ❌ Доступ запрещен:', accessCheck.reason);
      
      // Устанавливаем флаг что доступ запрещен
      window.__accessDenied = true;
      window.__accessGranted = false;
      
      // Показываем заглушку
      showAccessDeniedStub(accessCheck.message || 'Доступ ограничен. Свяжитесь с командой, чтобы получить доступ.');
      
      // ВАЖНО: НЕ инициализируем приложение - выходим СРАЗУ
      console.log('[initLegacyApp] 🛑 Stopping initialization - access denied');
      return;
    }
    
    // Если доступ есть - fetch уже восстановлен в checkUserAccess
    // Просто помечаем что доступ разрешен
    window.__accessGranted = true;
    window.__accessDenied = false;
    
    console.log('[initLegacyApp] ✅ Доступ разрешен, продолжаем инициализацию...');
  } catch (error) {
    console.error('[initLegacyApp] ❌ Ошибка проверки доступа:', error);
    console.error('[initLegacyApp] Error stack:', error.stack);
    // В случае ошибки продолжаем инициализацию (fail-open)
    if (window.__originalFetch) {
      window.fetch = window.__originalFetch;
    }
  }
  
  // Инициализируем оптимизации для мобильных устройств
  try {
    initMobileOptimizations();
  } catch (error) {
    console.warn('[init] Failed to init mobile optimizations:', error);
  }
  
  console.log('[initLegacyApp] 🚀 Начало инициализации...', {
    hasTelegram: !!window.Telegram,
    hasWebApp: !!(window.Telegram && window.Telegram.WebApp),
    hasInitData: !!(window.Telegram?.WebApp?.initData),
    timestamp: new Date().toISOString()
  });
  
  // Инициализируем Telegram WebApp
  initTelegramWebApp();
  
  // Проверяем наличие Telegram WebApp
  const m = hasTelegramWebApp();
  const h = hasTelegramUserAgent();
  const x = document.getElementById("main-app");
  /**
   * Показывает overlay с сообщением об ограничении доступа
   * 
   * Обертка над showTelegramOverlay для обратной совместимости.
   * Скрывает основной контент приложения (x) при показе overlay.
   * 
   * @param {string} message - Сообщение для отображения
   * @param {boolean} [showBotButton=true] - Показывать ли кнопку бота
   * @param {string} [title="Доступ ограничен"] - Заголовок overlay
   * @returns {void}
   */
  function $(message, showBotButton = !0, title = "Доступ ограничен") {
    showTelegramOverlay(message, showBotButton, title);
    if (x) {
      x.style.display = "none";
    }
  }
  /**
   * Скрывает overlay и показывает основной контент
   * 
   * Обертка над hideTelegramOverlay для обратной совместимости.
   * Показывает основной контент приложения (x) после скрытия overlay.
   * Использует множественные задержки для надежности на Android/Windows.
   * 
   * @returns {void}
   */
  function y() {
    hideTelegramOverlay();
    if (x) {
      x.style.display = "block";
      x.style.visibility = "visible";
    }
    // Дополнительно скрываем overlay через небольшие задержки для надежности (особенно для Android/Windows)
    setTimeout(() => {
      hideTelegramOverlay();
      if (x) {
        x.style.display = "block";
        x.style.visibility = "visible";
      }
    }, 100);
    setTimeout(() => {
      hideTelegramOverlay();
    }, 300);
    setTimeout(() => {
      hideTelegramOverlay();
      if (x) {
        x.style.display = "block";
        x.style.visibility = "visible";
      }
    }, 500);
    setTimeout(() => {
      hideTelegramOverlay();
    }, 1000);
  }
  if (
    (!m &&
      !h &&
      $(
        "Это мини-приложение доступно только внутри Telegram. Откройте @Glitchlings_bot и запустите его там.",
        !0,
        "Недоступно в браузере",
      ),
    x && (x.style.display = "none"),
    !m)
  ) {
    (showTelegramOverlay(
      "Это мини-приложение работает только внутри Telegram. Запустите его через нашего бота.",
      !0,
    ),
      console.warn(
        "[Glitchlings] Telegram WebApp API не обнаружен. Запуск возможен только через Telegram.",
      ));
    return;
  }

  // Не показываем overlay сразу - пусть bootstrap manager решает, когда показывать
  // showTelegramOverlay(
  //   "Подождите несколько секунд, мы инициализируем Telegram WebApp.",
  //   !1,
  // );
  
  // Глобальные обработчики ошибок теперь настраиваются через errorHandler
  // Старый код удален
  
  // Настраиваем глобальные обработчики ошибок
  const globalErrorHandlers = setupGlobalErrorHandlers({
    getTelegramWebApp: getTelegramWebAppSafe,
    showErrorAlert: true,
    logErrors: true,
  });
  globalErrorHandlers.setup();
  
  // Инициализируем утилиты для улучшения UX
  const loadingIndicatorManager = createLoadingIndicatorManager();
  const domCacheManager = createDOMCacheManager();
  let userMessageManager = createUserMessageManager({
    getTelegramWebApp: getTelegramWebAppSafe,
    showLootboxNotification: null // Будет установлен позже, когда lootboxManager будет создан
  });
  
  // Подключаем менеджеры к API
  setLoadingIndicatorManager(loadingIndicatorManager);
  setUserMessageManager(userMessageManager);
  
  let apiUrl = resolveApiUrl();
  const M = window.UPGRADE_METHODS || {};
  // Используем escapeHtml из модуля
  const k = escapeHtml;
  // Ht - словарь названий типов существ
  const Ht = TYPE_NAMES || {};
  // bn - массив AI пресетов для выпадающего списка противника
  let bn = (BATTLE_AI_TEMPLATES || []).map((template) => ({
    id: template.id,
    label: template.label,
    combatant: template.combatant,
    summary: template.summary,
    highlightTitle: template.highlightTitle,
    previewImage: template.previewImage
  }));
  // Используем константы из модуля
  const tt = DEFAULT_CREATURE_STATS;
  const je = STAT_LIMITS;

  // Создаём начальное состояние боя и PvP
  const w = createInitialBattleState();
  const H = createInitialPvpState();

  // ============================================
  // ГЛОБАЛЬНОЕ СОСТОЯНИЕ ПРИЛОЖЕНИЯ
  // ============================================
  // Документация переменных состояния (подробнее: legacy/state/globalState.js):
  // - d (currentUser) - текущий пользователь из БД
  // - q (initData) - Telegram initData (строка с данными авторизации)
  // - E (isAuthInProgress) - флаг авторизации в процессе
  // - D (isAuthCompleted) - флаг завершения авторизации
  // - N (attemptCount) - счетчик попыток авторизации
  // - S (stateObject) - вспомогательный объект состояния
  // - I - дополнительная переменная для отслеживания
  // - G - дополнительная переменная для флагов
  // - Y - коллекция существ (для PvP и других целей)
  // - Ot - время последней перезагрузки (используется в замыкании)
  // - J, X - вспомогательные переменные (var для hoisting, используются в замыканиях)
  // ============================================
  
  let handleInvalidInitDataRef = null;  // ссылка на обработчик невалидного initData
  let processAchievementPayloadRef = null;  // ссылка на обработчик достижений

  // Объявляем переменные ДО создания замыканий, чтобы избежать TDZ
  // Используем var для J и X, так как они используются в замыканиях
  let d = null,      // currentUser - текущий пользователь из БД
    q = null,        // initData - Telegram initData (строка с данными авторизации)
    E = !1,          // isAuthInProgress - флаг авторизации в процессе
    D = !1,          // isAuthCompleted - флаг завершения авторизации
    N = 0,           // attemptCount - счетчик попыток авторизации
    S = null,        // stateObject - вспомогательный объект состояния
    I = 0,           // дополнительная переменная для отслеживания
    G = !1,          // дополнительная переменная для флагов
    Y = [],          // collection - коллекция существ (для PvP и других целей)
    Ot = 0;          // lastReloadTime - время последней перезагрузки (используется в замыкании)
  
  // Объявляем J и X через var для hoisting, так как они используются в замыканиях
  // Инициализируем сразу, чтобы избежать ошибок "Cannot access before initialization"
  var J = null;      // helperVar1 - вспомогательная переменная (var для hoisting)
  var X = null;      // helperVar2 - вспомогательная переменная (var для hoisting)
  
  // Убеждаемся, что переменные доступны глобально для избежания проблем с минификатором
  if (typeof window !== 'undefined') {
    window.__glitchlingsJ = J;
    window.__glitchlingsX = X;
  }

  // Объявляем функции через var для hoisting, чтобы избежать TDZ в минифицированном коде
  // Это гарантирует, что функции будут доступны в замыканиях до их определения
  var updateUserBalancesUIWrapper = function() {
    updateUserBalancesUI(d, document);
  };
  
  // Объявляем $e как var для hoisting (будет переопределено позже)
  var $e = null;

  // Создаем обертку для safeFetch с обработкой ошибок
  const safeFetchWrapper = createSafeFetchWrapper({
    baseSafeFetch,
    handleInvalidInitData: (error) => {
      if (handleInvalidInitDataRef) {
        handleInvalidInitDataRef(error);
      }
    },
    processAchievementPayload: (data) => {
      if (processAchievementPayloadRef) {
        processAchievementPayloadRef(data);
      }
    },
  });

  const { safeFetchWithEnhancements } = safeFetchWrapper;

  const telegramAuth = createTelegramAuthManager({
    windowRef: window,
    showOverlay: showTelegramOverlay,
    hideOverlay: hideTelegramOverlay,
    safeFetch: safeFetchWithEnhancements,
    resolveApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    setTelegramWebApp: setTelegramWebAppSafe,
    getCurrentUser: () => d,
    setCurrentUser: (user) => {
      d = user;
    },
    getInitData: () => q,
    setInitData: (value) => {
      q = value;
    },
    isAuthInProgress: () => E,
    setAuthInProgress: (value) => {
      E = value;
    },
    isAuthCompleted: () => D,
    setAuthCompleted: (value) => {
      D = value;
    },
    getAttemptCount: () => N,
    setAttemptCount: (value) => {
      N = value;
    },
    getLastReloadAt: () => Ot,
    setLastReloadAt: (value) => {
      Ot = value;
    },
    onAuthSuccess: () => {
      y();
      updateUserBalancesUIWrapper();
      // $e объявлен как var выше, поэтому доступен здесь
      if ($e && typeof $e === "function") {
        $e();
      }
    },
  });

  const {
    buildAuthHeaders,
    handleInvalidInitData,
    initAuth,
    scheduleAuthRetry,
    isAccessDenied,
  } = telegramAuth;
  
  // Инициализируем проверку доступа для safeFetch
  // Это заблокирует все API запросы после получения access_denied
  setAccessDeniedChecker(isAccessDenied);
  
  // Инициализируем buildAuthHeaders для админ-панели
  setBuildAuthHeaders(() => buildAuthHeaders());

  handleInvalidInitDataRef = handleInvalidInitData;

  // Achievement notifications manager (создаём до createTelegramBootstrapManager, так как он использует ensureAchievementFeedPolling)
  const achievementNotifications = createAchievementNotificationManager({
    windowRef: window,
    documentRef: document,
    fetchFn: safeFetchWithEnhancements,
    getAuthHeaders: () => buildAuthHeaders(),
    getApiUrl: () => apiUrl,
  });

  const {
    dismissNotification: dismissAchievementNotification,
    processPayload: processAchievementPayload,
    schedulePoll: scheduleAchievementFeedPoll,
    ensurePolling: ensureAchievementFeedPolling,
  } = achievementNotifications;

  // Объявляем showScreen заранее, чтобы избежать проблем с TDZ
  // Будет переопределена позже при создании router
  let showScreen = function(screenName) {
    console.warn('[initLegacyApp] showScreen called before router initialization:', screenName);
  };
  
  // Флаг для отслеживания инициализации router
  let routerInitialized = false;
  
  // Используем объект для хранения функции, чтобы обертка всегда использовала актуальную функцию
  const showScreenRef = {
    fn: function(screenName) {
      console.warn('[initLegacyApp] showScreen called before router initialization:', screenName);
    }
  };
  
  // Создаем обертку для showScreen, которая будет использовать актуальную функцию из объекта
  const showScreenWrapper = function(screenName) {
    console.log('[showScreenWrapper] 🔄 Called with:', screenName, {
      routerInitialized,
      showScreenType: typeof showScreenRef.fn
    });
    
    if (routerInitialized && typeof showScreenRef.fn === 'function') {
      console.log('[showScreenWrapper] ✅ Calling showScreen');
      showScreenRef.fn(screenName);
    } else {
      console.warn('[showScreenWrapper] ⏳ Router not initialized yet, deferring call');
      // Если router еще не создан, сохраняем вызов для выполнения позже
      setTimeout(() => {
        if (typeof showScreenRef.fn === 'function') {
          console.log('[showScreenWrapper] ✅ Deferred call to showScreen');
          showScreenRef.fn(screenName);
        } else {
          console.error('[showScreenWrapper] ❌ showScreen is not a function after timeout');
        }
      }, 100);
    }
  };
  
  const { ensureTelegramBootstrapped, reconfigureBackButton } = createTelegramBootstrapManager({
    getCurrentUser: () => d,
    windowRef: window,
    documentRef: document,
    showOverlay: showTelegramOverlay,
    hideOverlay: hideTelegramOverlay,
    scheduleAuthRetry,
    ensureAchievementFeedPolling,
    showScreen: showScreenWrapper,
    getShowScreenRef: () => showScreenRef, // Передаем ссылку на объект для получения актуальной функции
    isAuthCompleted: () => D,
    getTelegramWebApp: getTelegramWebAppSafe,
    setTelegramWebApp: setTelegramWebAppSafe,
    mainAppContainer: x,
  });

  // Glitchlings Battle functions - определяем ДО screenHandlers
  window.GLITCHLINGS_HEROES = GLITCHLINGS_HEROES;
  
  // Функция загрузки экрана новостей (определяем ДО screenHandlers)
  function loadNewsScreen() {
    const newsList = document.getElementById('news-list');
    if (!newsList) {
      console.warn('[loadNewsScreen] news-list element not found');
      return;
    }
    
    // Обновляем имя пользователя
    const playerName = document.getElementById('player-name-news');
    if (playerName) {
      const user = getCurrentUser();
      if (user) {
        const telegramWebApp = getTelegramWebAppSafe();
        const firstName = telegramWebApp?.initDataUnsafe?.user?.first_name || user.username || 'GLITCHLING';
        playerName.textContent = firstName.toUpperCase();
      }
    }
    
    // Показываем загрузку
    newsList.innerHTML = '<div style="padding: 40px; text-align: center; color: var(--tg-theme-hint-color);">Загрузка новостей...</div>';
    
    // Загружаем новости
    setTimeout(() => {
      const news = [
        {
          id: 1,
          type: 'update',
          title: '🎉 Обновление игры',
          date: new Date().toLocaleDateString('ru-RU'),
          content: 'Добро пожаловать в Glitchlings! Игра находится в активной разработке. Следите за обновлениями!',
          icon: '🎮'
        },
        {
          id: 2,
          type: 'event',
          title: '⚔️ Новый режим боя',
          date: new Date(Date.now() - 86400000).toLocaleDateString('ru-RU'),
          content: 'Попробуйте новый мультиплеерный режим боя! Сражайтесь с другими игроками в реальном времени.',
          icon: '⚔️'
        },
        {
          id: 3,
          type: 'feature',
          title: '📦 Система лутбоксов',
          date: new Date(Date.now() - 172800000).toLocaleDateString('ru-RU'),
          content: 'Открывайте лутбоксы и получайте награды! Ежедневная бесплатная коробка доступна каждому игроку.',
          icon: '📦'
        },
        {
          id: 4,
          type: 'tip',
          title: '💡 Совет дня',
          date: new Date(Date.now() - 259200000).toLocaleDateString('ru-RU'),
          content: 'Не забывайте собирать ежедневные награды и открывать бесплатные коробки. Это поможет вам быстрее развиваться!',
          icon: '💡'
        }
      ];
      
      if (news.length === 0) {
        newsList.innerHTML = '<div style="padding: 40px; text-align: center; color: var(--tg-theme-hint-color);">Новостей пока нет</div>';
        return;
      }
      
      newsList.innerHTML = news.map(item => `
        <div class="news-item" style="
          background: var(--tg-theme-secondary-bg-color, #f5f5f5);
          border-radius: 12px;
          padding: 16px;
          border: 1px solid var(--tg-theme-hint-color, #e0e0e0);
        ">
          <div style="display: flex; align-items: flex-start; gap: 12px;">
            <div style="
              font-size: 32px;
              flex-shrink: 0;
              width: 48px;
              height: 48px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: var(--tg-theme-bg-color, #fff);
              border-radius: 12px;
            ">${item.icon}</div>
            <div style="flex: 1; min-width: 0;">
              <div style="
                font-weight: 700;
                font-size: 16px;
                margin-bottom: 8px;
                color: var(--tg-theme-text-color);
              ">${item.title}</div>
              <div style="
                font-size: 12px;
                color: var(--tg-theme-hint-color);
                margin-bottom: 8px;
              ">${item.date}</div>
              <div style="
                font-size: 14px;
                color: var(--tg-theme-text-color);
                line-height: 1.5;
              ">${item.content}</div>
            </div>
          </div>
        </div>
      `).join('');
    }, 500);
  }
  
  window.loadGlitchlingsBattleScreen = function() {
    const selectionContainer = document.getElementById('glitchlings-hero-selection');
    const battleContainer = document.getElementById('glitchlings-battle-container');
    
    if (!selectionContainer) {
      console.error('[Glitchlings] Selection container not found');
      return;
    }
    
    // Сбрасываем состояние
    window.selectedGlitchlingsHero = null;
    if (battleContainer) {
      battleContainer.style.display = 'none';
    }
    if (selectionContainer) {
      selectionContainer.style.display = 'grid';
    }
    
    // Рендерим выбор героев
    selectionContainer.innerHTML = '';
    
    // Создаём отдельную сетку для героев
    const heroGrid = document.createElement('div');
    heroGrid.className = 'glitchlings-hero-grid';
    
    Object.values(GLITCHLINGS_HEROES).forEach(hero => {
      const card = document.createElement('div');
      card.className = 'glitchlings-hero-card';
      card.onclick = () => {
        document.querySelectorAll('.glitchlings-hero-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        window.selectedGlitchlingsHero = hero.id;
      };
      card.innerHTML = `
        <div class="glitchlings-hero-card-visual">${hero.visual}</div>
        <div class="glitchlings-hero-card-name">${hero.name}</div>
        <div class="glitchlings-hero-card-role">${hero.role}</div>
      `;
      heroGrid.appendChild(card);
    });
    
    selectionContainer.appendChild(heroGrid);
    
    // Кнопка старта боя
    const startBtn = document.createElement('button');
    startBtn.className = 'modern-btn';
    startBtn.textContent = '🌐 Начать мультиплеерный бой';
    startBtn.style.width = '100%';
    startBtn.style.marginTop = '16px';
    startBtn.onclick = () => {
      if (!window.selectedGlitchlingsHero) {
        const telegramWebApp = getTelegramWebAppSafe();
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert('Выбери героя!');
        }
        return;
      }
      
      // Всегда запускаем мультиплеерный режим
      startMultiplayerBattle(window.selectedGlitchlingsHero);
    };
    selectionContainer.appendChild(startBtn);
  };

  const screenHandlers = {
    pass: () => {
      if (typeof window.loadProgressScreen === 'function') {
        window.loadProgressScreen();
      }
      updateUserBalancesUI();
    },
    glitchlingsbattle: () => {
      if (typeof window.loadGlitchlingsBattleScreen === 'function') {
        window.loadGlitchlingsBattleScreen();
      }
      updateUserBalancesUI();
    },
    errorboxes: () => {
      if (typeof window.loadErrorBoxesScreen === 'function') {
        window.loadErrorBoxesScreen();
      }
      updateUserBalancesUI();
    },
    'lootbox-inventory': () => {
      if (typeof window.loadLootboxInventory === 'function') {
        window.loadLootboxInventory();
      }
      updateUserBalancesUI();
    },
    energy: () => {
      if (typeof window.loadEnergyManager === 'function') {
        window.loadEnergyManager();
      }
      updateUserBalancesUI();
    },
    exp: () => {
      if (typeof window.loadExpManager === 'function') {
        window.loadExpManager();
      }
      updateUserBalancesUI();
    },
    home: () => {
      if (typeof $e === "function") {
        $e();
      }
    },
    news: () => {
      loadNewsScreen();
      updateUserBalancesUI();
    },
    store: () => {
      if (typeof lt === "function") {
        lt();
      }
      // Показываем таб OFFERS по умолчанию
      setTimeout(() => {
        if (typeof window.showShopTab === 'function') {
          window.showShopTab('offers');
        }
      }, 100);
    },
    collection: () => {
      if (typeof dt === "function") {
        dt();
      }
      if (typeof Ut === "function") {
        Ut();
      }
    },
    gifts: () => {
      if (typeof De === "function") {
        De();
      }
    },
    battle: () => {
      if (typeof rn === "function") {
        rn();
      }
      // Инициализируем formationManager при показе экрана боя
      if (typeof window.__formationManager === "object" && window.__formationManager !== null) {
        try {
          window.__formationManager.init();
          console.log('[initLegacyApp] FormationManager initialized on battle screen');
        } catch (error) {
          console.error('[initLegacyApp] Error initializing FormationManager:', error);
        }
      }
    },
    payment: () => {
      if (typeof kt === "function") {
        kt();
      }
    },
    "royalty-info": () => {
      if (typeof en === "function") {
        en();
      }
    },
    transactions: () => {
      if (typeof loadTransactions === "function") {
        loadTransactions();
      }
    },
    "royalty-stats": () => {
      if (typeof loadRoyaltyStats === "function") {
        loadRoyaltyStats();
      }
    },
    achievements: () => {
      // Получаем текущий фильтр из менеджера достижений или используем значение по умолчанию
      const currentFilter = typeof getAchievementsCurrentFilter === "function" 
        ? getAchievementsCurrentFilter() 
        : (typeof At !== "undefined" && At ? At : "all");
      if (typeof Pt === "function") {
        Pt(currentFilter);
      }
    },
    errorborn: () => {
      if (typeof rt === "function") {
        rt(!0);
      }
    },
    admin: async () => {
      if (typeof renderAdminScreen === "function") {
        await renderAdminScreen();
      }
    },
    club: () => {
      if (typeof window.loadClubScreen === 'function') {
        window.loadClubScreen();
      }
      updateUserBalancesUI();
    },
  };

  // Переопределяем showScreen реальной функцией из router
  const { showScreen: routerShowScreen } = createLegacyRouter({
    windowRef: window,
    documentRef: document,
    getTelegramWebApp: getTelegramWebAppSafe,
    resetBattleView: (options) => {
      if (typeof Ge === "function") {
        Ge(options);
      }
    },
    screenHandlers,
  });
  
  // Обновляем showScreen реальной функцией из router
  showScreen = routerShowScreen;
  // Обновляем функцию в объекте, чтобы обертка использовала актуальную функцию
  showScreenRef.fn = routerShowScreen;
  // Делаем showScreen доступной глобально для onclick атрибутов в HTML
  window.showScreen = routerShowScreen;
  
  // Pass/Progress screen functions (будут переопределены после инициализации progressPassManager)
  
  // Shop tabs function
  window.showShopTab = function(tabName) {
    const tabs = document.querySelectorAll('.shop-tab');
    const contents = document.querySelectorAll('.shop-tab-content');
    
    tabs.forEach(tab => tab.classList.remove('active'));
    contents.forEach(content => {
      content.classList.remove('active');
      content.style.display = 'none';
    });
    
    const activeTab = document.getElementById(`shop-tab-${tabName}`);
    const activeContent = document.getElementById(`shop-tab-${tabName}-content`);
    
    if (activeTab) {
      activeTab.classList.add('active');
    }
    if (activeContent) {
      activeContent.classList.add('active');
      activeContent.style.display = 'block';
    }
    
    // Загружаем контент таба
    if (tabName === 'boxes' && typeof window.loadShopBoxes === 'function') {
      window.loadShopBoxes();
    } else if (tabName === 'resources' && typeof window.loadShopResources === 'function') {
      window.loadShopResources();
    }
  };
  
  // Load shop boxes tab - загружает коробки из API
  window.loadShopBoxes = async function() {
    const container = document.getElementById('shop-boxes');
    if (!container) return;
    
    container.innerHTML = '<div class="loading" style="grid-column: 1 / -1; text-align: center; padding: 40px;">Загрузка коробок...</div>';
    
    try {
      // Получаем список лутбоксов из менеджера
      const { lootboxes, dailyLootboxAvailable } = await getLootboxesListFromManager();
      
      if (!Array.isArray(lootboxes) || lootboxes.length === 0) {
        container.innerHTML = '<div class="error" style="grid-column: 1 / -1; text-align: center; padding: 40px;">Коробки временно недоступны</div>';
        return;
      }
      
      container.innerHTML = '';
      
      // Маппинг кодов лутбоксов на иконки и описания
      const lootboxInfo = {
        'daily_free_box': { icon: '🎁', badge: 'DAILY' },
        'glitch_box': { icon: '📦' },
        'error_box': { icon: '💥', badge: 'BEST' },
        'system_crash_box': { icon: '💀' }
      };
      
      lootboxes.forEach(lootbox => {
        const info = lootboxInfo[lootbox.code] || { icon: '📦' };
        const isDaily = lootbox.code === 'daily_free_box';
        const canOpen = isDaily ? dailyLootboxAvailable : true;
        
        const item = document.createElement('div');
        item.className = `shop-item ${!canOpen && isDaily ? 'disabled' : ''}`;
        
        if (canOpen) {
          item.style.cursor = 'pointer';
          item.onclick = () => {
            // Ежедневные коробки открываются сразу, остальные покупаются (добавляются в инвентарь)
            if (isDaily) {
              handleOpenLootboxFromManager(lootbox.code);
            } else {
              // Используем функцию покупки из менеджера
              if (buyLootboxFromStoreFromManager) {
                buyLootboxFromStoreFromManager(lootbox.code);
              } else {
                console.error('[loadShopBoxes] Функция покупки не найдена');
              }
            }
          };
        } else {
          item.style.cursor = 'not-allowed';
        }
        
        let priceHtml = '';
        if (lootbox.cost_type === 'free') {
          if (canOpen) {
            priceHtml = `<div class="item-price price-free">БЕСПЛАТНО</div>`;
          } else {
            // Получаем время до следующего доступного лутбокса
            let timeLeft = '';
            const getDailyLootboxNextAvailableFn = window.getDailyLootboxNextAvailableFromManager;
            if (getDailyLootboxNextAvailableFn) {
              const nextAvailable = getDailyLootboxNextAvailableFn();
              if (nextAvailable) {
                // Форматируем время
                const now = new Date();
                const next = new Date(nextAvailable);
                const diff = next - now;
                
                if (diff > 0) {
                  const hours = Math.floor(diff / (1000 * 60 * 60));
                  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                  
                  if (hours > 0) {
                    timeLeft = `${hours}ч ${minutes}м`;
                  } else if (minutes > 0) {
                    timeLeft = `${minutes}м ${seconds}с`;
                  } else {
                    timeLeft = `${seconds}с`;
                  }
                }
              }
            }
            
            if (timeLeft) {
              priceHtml = `<div class="item-price price-free" style="display: flex; flex-direction: column; gap: 4px; align-items: center;">
                <div>УЖЕ ПОЛУЧЕНО</div>
                <div class="daily-timer" style="font-size: 11px; opacity: 0.8; margin-top: 2px;">Доступно через: ${timeLeft}</div>
              </div>`;
            } else {
              priceHtml = `<div class="item-price price-free">УЖЕ ПОЛУЧЕНО</div>`;
            }
          }
        } else if (lootbox.cost_type === 'coins') {
          priceHtml = `<div class="item-price price-coins">💰 ${lootbox.cost_amount}</div>`;
        } else if (lootbox.cost_type === 'stars') {
          priceHtml = `<div class="item-price price-stars">⭐ ${lootbox.cost_amount}</div>`;
        }
        
        item.innerHTML = `
          ${info.badge ? `<div class="item-badge">${info.badge}</div>` : ''}
          <div class="item-icon">${info.icon}</div>
          <div class="item-name">${lootbox.name}</div>
          <div class="item-desc" style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 4px;">${lootbox.description || ''}</div>
          ${priceHtml}
        `;
        container.appendChild(item);
        
        // Запускаем таймер обновления для ежедневного лутбокса, если он недоступен
        if (isDaily && !canOpen) {
          const getDailyLootboxNextAvailableFn = window.getDailyLootboxNextAvailableFromManager;
          const timerEl = item.querySelector('.daily-timer');
          if (timerEl && getDailyLootboxNextAvailableFn) {
            const updateTimer = () => {
              const nextAvailable = getDailyLootboxNextAvailableFn();
              if (nextAvailable) {
                const now = new Date().getTime();
                const next = new Date(nextAvailable).getTime();
                const diff = next - now;
                
                if (diff > 0) {
                  const hours = Math.floor(diff / (1000 * 60 * 60));
                  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                  
                  let newTimeLeft = '';
                  if (hours > 0) {
                    newTimeLeft = `${hours}ч ${minutes}м ${seconds}с`;
                  } else if (minutes > 0) {
                    newTimeLeft = `${minutes}м ${seconds}с`;
                  } else {
                    newTimeLeft = `${seconds}с`;
                  }
                  
                  timerEl.textContent = `Доступно через: ${newTimeLeft}`;
                  setTimeout(updateTimer, 1000);
                } else {
                  // Время истекло, перезагружаем список
                  window.loadShopBoxes();
                }
              }
            };
            setTimeout(updateTimer, 1000);
          }
        }
      });
    } catch (error) {
      console.error('[loadShopBoxes] Ошибка:', error);
      container.innerHTML = '<div class="error" style="grid-column: 1 / -1; text-align: center; padding: 40px;">Ошибка загрузки коробок</div>';
    }
  };
  
  // Load shop resources tab
  window.loadShopResources = function() {
    const container = document.getElementById('shop-resources');
    if (!container) return;
    
    const resources = [
      {
        id: 'power-10',
        icon: '⚡',
        name: 'Энергия x10',
        price: '50',
        priceType: 'coins',
        currency: '💰'
      },
      {
        id: 'power-30',
        icon: '⚡',
        name: 'Энергия x30',
        price: '120',
        priceType: 'coins',
        currency: '💰'
      },
      {
        id: 'coins-100',
        icon: '💰',
        name: 'Монеты x100',
        price: '10',
        priceType: 'stars',
        currency: '⭐'
      },
      {
        id: 'coins-500',
        icon: '💰',
        name: 'Монеты x500',
        price: '40',
        priceType: 'stars',
        currency: '⭐',
        badge: 'BEST'
      }
    ];
    
    container.innerHTML = '';
    resources.forEach(resource => {
      const item = document.createElement('div');
      item.className = 'shop-item';
      
      let priceHtml = '';
      if (resource.priceType === 'free') {
        priceHtml = `<div class="item-price price-free">${resource.price}</div>`;
      } else {
        priceHtml = `<div class="item-price price-${resource.priceType}">${resource.currency} ${resource.price}</div>`;
      }
      
      item.innerHTML = `
        ${resource.badge ? `<div class="item-badge">${resource.badge}</div>` : ''}
        <div class="item-icon" style="font-size: 48px; margin-bottom: 8px;">${resource.icon}</div>
        <div class="item-name" style="font-weight: 600; margin-bottom: 4px; font-size: 14px;">${resource.name}</div>
        <div class="item-desc" style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 8px;">
          ${resource.id.includes('power') ? 'Добавляет энергию всем существам' : 'Пополняет баланс монет'}
        </div>
        ${priceHtml}
        <button class="modern-btn" style="margin-top: 8px; width: 100%; padding: 10px; font-size: 13px; font-weight: 600;" onclick="buyResource('${resource.id}')">КУПИТЬ</button>
      `;
      container.appendChild(item);
    });
  };
  
  window.buyPremiumPass = async function() {
    const telegramWebApp = getTelegramWebAppSafe();
    
    if (!telegramWebApp) {
      alert('Ошибка: Telegram WebApp не доступен');
      return;
    }
    
    // Проверяем, есть ли уже премиум-пасс
    const currentUser = d; // Получаем текущего пользователя
    if (currentUser && currentUser.premium_pass_active === true) {
      const lootboxManager = window.lootboxManager;
      if (lootboxManager && lootboxManager.showLootboxNotification) {
        lootboxManager.showLootboxNotification(
          '⭐ Премиум-пасс активен',
          'У вас уже есть активный премиум-пасс!\n\nЭксклюзивные существа и двойные награды доступны.'
        );
      } else if (telegramWebApp.showAlert) {
        telegramWebApp.showAlert('У вас уже есть активный премиум-пасс!');
      } else {
        alert('У вас уже есть активный премиум-пасс!');
      }
      return;
    }
    
    // Показываем подтверждение (асинхронно через Promise)
    const confirmed = await new Promise((resolve) => {
      if (telegramWebApp.showConfirm) {
        try {
          telegramWebApp.showConfirm(
            'Купить премиум-пасс за 169 ⭐?\n\nВы получите:\n• Эксклюзивные существа\n• +50 Звёзд мгновенно\n• Двойные награды',
            (result) => resolve(result === true)
          );
        } catch (error) {
          console.warn('[buyPremiumPass] showConfirm error:', error);
          resolve(window.confirm ? window.confirm('Купить премиум-пасс за 169 ⭐?') : true);
        }
      } else {
        resolve(window.confirm ? window.confirm('Купить премиум-пасс за 169 ⭐?') : true);
      }
    });
    
    if (!confirmed) {
      return;
    }
    
    try {
      const currentApiUrl = apiUrl || resolveApiUrl();
      const response = await safeFetchWithEnhancements(`${currentApiUrl}/progress/premium/buy`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        'Content-Type': 'application/json'
      });
      
      if (response.success && response.data?.success) {
        // Обновляем данные пользователя из ответа API
        const data = response.data;
        const currentUser = d; // Получаем текущего пользователя
        if (currentUser && data.user) {
          const updatedUser = { ...currentUser, ...data.user };
          d = updatedUser; // Обновляем глобальное состояние
          updateUserBalancesUI(updatedUser);
        } else if (data.user) {
          // Если нет текущего пользователя, обновляем из ответа
          updateUserBalancesUI(data.user);
        } else {
          // Если данных нет в ответе, обновляем из текущего состояния
          updateUserBalancesUI(currentUser);
        }
        
        // Показываем успех через кастомное уведомление
        const lootboxManager = window.lootboxManager;
        if (lootboxManager && lootboxManager.showLootboxNotification) {
          lootboxManager.showLootboxNotification(
            '⭐ Премиум-пасс активирован!',
            `+50 ⭐ начислено в подарок\n\nЭксклюзивные существа и двойные награды теперь доступны!`
          );
        } else if (telegramWebApp.showAlert) {
          telegramWebApp.showAlert(data.message || 'Премиум-пасс активирован!');
        }
        
        // Обновляем экран прогресса, если он открыт
        if (typeof window.loadProgressScreen === 'function') {
          setTimeout(() => {
            window.loadProgressScreen();
          }, 500);
        }
      } else {
        const errorMessage = response.data?.message || response.error || 'Ошибка при покупке премиум-пасса';
        const errorCode = response.data?.error;
        
        // Специальная обработка для "уже есть премиум-пасс"
        if (errorCode === 'already_has_premium') {
          const lootboxManager = window.lootboxManager;
          if (lootboxManager && lootboxManager.showLootboxNotification) {
            lootboxManager.showLootboxNotification(
              '⭐ Премиум-пасс активен',
              'У вас уже есть активный премиум-пасс!\n\nЭксклюзивные существа и двойные награды доступны.'
            );
          } else if (telegramWebApp.showAlert) {
            telegramWebApp.showAlert('У вас уже есть активный премиум-пасс!');
          }
          
          // Обновляем данные пользователя, если они пришли
          const data = response.data;
          if (data?.user) {
            const currentUser = d;
            if (currentUser) {
              const updatedUser = { ...currentUser, ...data.user };
              d = updatedUser;
              updateUserBalancesUI(updatedUser);
            }
          }
          
          // Обновляем экран прогресса
          if (typeof window.loadProgressScreen === 'function') {
            setTimeout(() => {
              window.loadProgressScreen();
            }, 500);
          }
        } else {
          // Показываем ошибку через кастомное уведомление
          const lootboxManager = window.lootboxManager;
          if (lootboxManager && lootboxManager.showLootboxNotification) {
            lootboxManager.showLootboxNotification('❌ Ошибка', errorMessage);
          } else if (telegramWebApp.showAlert) {
            telegramWebApp.showAlert(errorMessage);
          } else {
            alert(errorMessage);
          }
        }
      }
    } catch (error) {
      console.error('[buyPremiumPass] Error:', error);
      const errorMessage = 'Ошибка при покупке премиум-пасса. Попробуйте позже.';
      // Показываем ошибку через кастомное уведомление
      const lootboxManager = window.lootboxManager;
      if (lootboxManager && lootboxManager.showLootboxNotification) {
        lootboxManager.showLootboxNotification('❌ Ошибка', errorMessage);
      } else if (telegramWebApp.showAlert) {
        telegramWebApp.showAlert(errorMessage);
      } else {
        alert(errorMessage);
      }
    }
  };
  
  // Buy resource function - будет инициализирована позже
  window.buyResource = function(resourceId) {
    console.warn('[initLegacyApp] buyResource called before initialization');
  };
  
  // Error Boxes functions
  window.openErrorBox = function() {
    const box = document.getElementById('errorbox-box');
    const rewards = document.getElementById('errorbox-rewards');
    const continueBtn = document.getElementById('errorbox-continue');
    
    if (box && !box.classList.contains('opening')) {
      box.classList.add('opening');
      box.style.cursor = 'not-allowed';
      
      setTimeout(() => {
        // Generate random rewards
        const rewardTypes = [
          { icon: '💰', name: 'МОНЕТЫ', amount: Math.floor(Math.random() * 100) + 50 },
          { icon: '⭐', name: 'ЗВЁЗДЫ', amount: Math.floor(Math.random() * 10) + 5 },
          { icon: '🎴', name: 'СУЩЕСТВО', amount: 1 }
        ];
        
        const selectedRewards = [];
        for (let i = 0; i < 3; i++) {
          selectedRewards.push(rewardTypes[Math.floor(Math.random() * rewardTypes.length)]);
        }
        
        rewards.innerHTML = '';
        selectedRewards.forEach(reward => {
          const rewardEl = document.createElement('div');
          rewardEl.className = 'reward-item';
          rewardEl.innerHTML = `
            <div class="reward-icon">${reward.icon}</div>
            <div class="reward-name">${reward.name}</div>
            <div class="reward-amount">${reward.amount}</div>
          `;
          rewards.appendChild(rewardEl);
        });
        
        rewards.style.display = 'grid';
        continueBtn.style.display = 'block';
        box.classList.remove('opening');
      }, 800);
    }
  };
  
  window.closeErrorBox = function() {
    const overlay = document.getElementById('errorbox-overlay');
    const rewards = document.getElementById('errorbox-rewards');
    const continueBtn = document.getElementById('errorbox-continue');
    const box = document.getElementById('errorbox-box');
    
    if (overlay) {
      overlay.style.display = 'none';
      overlay.classList.remove('active');
    }
    if (rewards) rewards.style.display = 'none';
    if (continueBtn) continueBtn.style.display = 'none';
    if (box) {
      box.classList.remove('opening');
      box.style.cursor = 'pointer';
    }
  };
  
  // Load Error Boxes screen
  // Glitchlings Battle functions - уже определены выше перед screenHandlers
  
  // Мультиплеерный бой
  let multiplayerManager = null;
  
  // Объект-ссылка для передачи в модуль
  const multiplayerManagerRef = { current: null };
  
  /**
   * Запускает мультиплеерный бой
   * 
   * Обертка над startMultiplayerBattleFromModule для обратной совместимости.
   * Сохраняет ссылку на менеджер мультиплеерного боя.
   * 
   * @param {string|number} playerHeroId - ID героя игрока
   * @returns {void}
   */
  function startMultiplayerBattle(playerHeroId) {
    const manager = startMultiplayerBattleFromModule(playerHeroId, getTelegramWebAppSafe, multiplayerManagerRef);
    multiplayerManager = manager || multiplayerManagerRef.current;
  }
  
  // Lootbox manager
  const lootboxManager = createLootboxManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    getCurrentUser: () => d,
    setCurrentUser: (user) => {
      d = user;
    },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
    showScreen: (screenName) => {
      // Используем showScreenWrapper, который всегда актуален
      if (typeof showScreenWrapper === 'function') {
        showScreenWrapper(screenName);
      } else if (typeof window.showScreen === 'function') {
        window.showScreen(screenName);
      }
    },
    showPaymentScreen: () => {
      // Zt будет обновлен позже, но мы можем использовать замыкание
      if (typeof window.showPaymentScreen === 'function') {
        window.showPaymentScreen();
      } else if (typeof showScreenWrapper === 'function') {
        showScreenWrapper('payment');
      } else if (typeof window.showScreen === 'function') {
        window.showScreen('payment');
      }
    },
  });
  
  // Обновляем userMessageManager с функцией показа уведомлений из lootboxManager
  if (lootboxManager && lootboxManager.showLootboxNotification) {
    userMessageManager = createUserMessageManager({
      getTelegramWebApp: getTelegramWebAppSafe,
      showLootboxNotification: lootboxManager.showLootboxNotification.bind(lootboxManager)
    });
    setUserMessageManager(userMessageManager);
  }

  const {
    loadErrorBoxesScreen: loadErrorBoxesScreenFromManager,
    closeLootboxOverlay: closeLootboxOverlayFromManager,
    getLootboxesList: getLootboxesListFromManager,
    handleOpenLootbox: handleOpenLootboxFromManager,
    buyLootboxFromStore: buyLootboxFromStoreFromManager,
    getDailyLootboxNextAvailable: getDailyLootboxNextAvailableFromManager
  } = lootboxManager;
  
  // Экспортируем lootboxManager глобально для использования в других функциях
  window.lootboxManager = lootboxManager;
  
  // Используем функцию из менеджера
  window.loadErrorBoxesScreen = loadErrorBoxesScreenFromManager;
  window.closeErrorBox = closeLootboxOverlayFromManager;
  
  // Экспортируем функцию для использования в loadShopBoxes
  window.getDailyLootboxNextAvailableFromManager = getDailyLootboxNextAvailableFromManager;
  
  // Инициализируем менеджер инвентаря лутбоксов
  const lootboxInventoryManager = createLootboxInventoryManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    getCurrentUser: () => d,
    setCurrentUser: (user) => { d = user; },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
  });

  const {
    renderInventory: renderLootboxInventory,
    openLootboxFromInventory: openLootboxFromInventoryFn
  } = lootboxInventoryManager;

  window.loadLootboxInventory = async function() {
    await renderLootboxInventory();
  };
  
  window.openLootboxFromInventory = async function(lootboxId) {
    const success = await openLootboxFromInventoryFn(lootboxId);
    if (success) {
      await renderLootboxInventory(); // Перезагружаем инвентарь
    }
  };

  // Инициализируем менеджер энергии
  const energyManager = createEnergyManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    getCurrentUser: () => d,
    setCurrentUser: (user) => { d = user; },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
    getUserCollection: async () => {
      const response = await safeFetchWithEnhancements(`${apiUrl}/user/me`, {
        headers: buildAuthHeaders(),
      });
      if (response.success && response.data?.success) {
        return response.data.collection || [];
      }
      return [];
    }
  });

  const {
    renderEnergyManager: renderEnergyManagerFn,
    applyEnergyToCreature: applyEnergyToCreatureFn
  } = energyManager;

  window.loadEnergyManager = async function() {
    await renderEnergyManagerFn();
  };
  
  window.applyEnergyToCreature = async function(creatureId, amount) {
    const success = await applyEnergyToCreatureFn(creatureId, amount);
    if (success) {
      await renderEnergyManagerFn(); // Перезагружаем экран
    }
  };

  // Инициализируем менеджер опыта
  const expManager = createExpManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    getCurrentUser: () => d,
    setCurrentUser: (user) => { d = user; },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
    getUserCollection: async () => {
      const response = await safeFetchWithEnhancements(`${apiUrl}/user/me`, {
        headers: buildAuthHeaders(),
      });
      if (response.success && response.data?.success) {
        return response.data.collection || [];
      }
      return [];
    }
  });

  const {
    renderExpManager: renderExpManagerFn,
    applyExpToCreature: applyExpToCreatureFn
  } = expManager;

  window.loadExpManager = async function() {
    await renderExpManagerFn();
  };
  
  window.applyExpToCreature = async function(creatureId, amount) {
    const success = await applyExpToCreatureFn(creatureId, amount);
    if (success) {
      await renderExpManagerFn(); // Перезагружаем экран
    }
  };
  
  // Инициализируем менеджер клуба
  const clubManager = createClubManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getCurrentUser: () => d,
    updateUserBalancesUI: updateUserBalancesUIWrapper
  });

  const {
    renderClubScreen: renderClubScreenFn
  } = clubManager;

  window.loadClubScreen = async function() {
    await renderClubScreenFn();
  };
  
  // Экспортируем функцию в window для глобального доступа
  window.loadNewsScreen = loadNewsScreen;
  
  routerInitialized = true;
  
  console.log('[initLegacyApp] ✅ Router initialized, showScreen updated');
  
  // Переконфигурируем кнопку "Назад" после инициализации router
  if (typeof reconfigureBackButton === 'function') {
    setTimeout(() => {
      reconfigureBackButton();
    }, 200);
  }

  // Achievement notifications уже создан выше, используем его здесь
  processAchievementPayloadRef = processAchievementPayload;

  window.dismissAchievementNotification = () => {
    dismissAchievementNotification({ auto: false });
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      scheduleAchievementFeedPoll(1000);
    }
  });
  const professionsService = createProfessionsService({
    safeFetch: safeFetchWithEnhancements,
    getAuthHeaders: () => buildAuthHeaders(),
    getApiUrl: () => apiUrl,
  });

  const {
    fetchProfessionsData,
    getProfessionMeta,
    getProfessionIcon,
    getProfessionName,
    getProfessionStatLabel,
    renderProfessionBadge,
  } = professionsService;

  // Ot уже объявлена выше, чтобы избежать проблем с TDZ в замыканиях
  
  // Объявляем переменные заранее, чтобы избежать проблем с TDZ
  // Они будут переопределены позже при создании соответствующих менеджеров
  let Cn = function() { console.warn('[initLegacyApp] Cn (claimErrorbornReward) called before initialization'); };
  let rt = function() { console.warn('[initLegacyApp] rt (loadErrorbornScreen) called before initialization'); };
  let Dn = function() { console.warn('[initLegacyApp] Dn (filterCollection) called before initialization'); };
  let DnInitialized = false;
  let Nn = function() { console.warn('[initLegacyApp] Nn (sortCollection) called before initialization'); };
  let NnInitialized = false;
  let Os = function() { console.warn('[initLegacyApp] Os (runBattleSimulation) called before initialization'); };
  let OsInitialized = false;
  let bs = function() { console.warn('[initLegacyApp] bs (showSendGiftScreen) called before initialization'); };
  let bsInitialized = false;
  let ws = function() { console.warn('[initLegacyApp] ws (sendGift) called before initialization'); };
  let wsInitialized = false;
  let De = function() { console.warn('[initLegacyApp] De (showIncomingGifts) called before initialization'); };
  let DeInitialized = false;
  let Xt = function() { console.warn('[initLegacyApp] Xt (showSentGifts) called before initialization'); };
  let XtInitialized = false;
  let $s = function() { console.warn('[initLegacyApp] $s (acceptGift) called before initialization'); };
  let $sInitialized = false;
  let _s = function() { console.warn('[initLegacyApp] _s (declineGift) called before initialization'); };
  let _sInitialized = false;
  let ks = function() { console.warn('[initLegacyApp] ks (claimDaily) called before initialization'); };
  let ksInitialized = false;
  let As = function() { console.warn('[initLegacyApp] As (buyPackage) called before initialization'); };
  let Ss = function() {
    if (!SsInitialized) {
      console.warn('[initLegacyApp] Ss (showProfessions) called before initialization');
      return;
    }
  };
  let SsInitialized = false;
  let Ts = function() {
    if (!TsInitialized) {
      console.warn('[initLegacyApp] Ts (assignProfession) called before initialization');
      return;
    }
  };
  let TsInitialized = false;
  let tn = function() { console.warn('[initLegacyApp] tn (showUpgrades) called before initialization'); };
  let tnInitialized = false;
  let Ls = function() { console.warn('[initLegacyApp] Ls (applyUpgrade) called before initialization'); };
  let Ye = function() { console.warn('[initLegacyApp] Ye (loadBattleHistory) called before initialization'); };
  let YeInitialized = false;
  let Ke = function() { console.warn('[initLegacyApp] Ke (loadPublicBattles) called before initialization'); };
  let Ws = function() { console.warn('[initLegacyApp] Ws (viewBattleDetail) called before initialization'); };
  let qe = function() { console.warn('[initLegacyApp] qe (renderErrorbornScreen) called before initialization'); };
  let qeInitialized = false;
  let at = function() { console.warn('[initLegacyApp] at (renderErrorbornQuestCard) called before initialization'); };
  let Hn = function() { console.warn('[initLegacyApp] Hn (renderCollectionPreview) called before initialization'); };
  let dn = function() { console.warn('[initLegacyApp] dn (loadAchievementsStats) called before initialization'); };
  let HnInitialized = false;
  let showAchievements = function() { console.warn('[initLegacyApp] showAchievements called before initialization'); };
  let showAchievementsInitialized = false;
  let dnInitialized = false;
  // Wn теперь берется из battleViewManager (определен ниже)
  let Wn;
  let Zt = function() { console.warn('[initLegacyApp] Zt (showPaymentScreen) called before initialization'); };
  // Используем функцию из модуля
  let Lt = function(presetId) {
    return findAiPreset(presetId, BATTLE_AI_TEMPLATES);
  };
  // It и Et теперь берутся из pvpManager (определены ниже)
  let It;
  let Et;
  let js;  // startPvpSearch
  let cn;  // cancelPvpSearch
  let Rs;  // pollPvpMatch
  let ne;  // updatePvpStatus
  let ze;  // updatePvpControls
  let ln;  // startPvpPolling
  let Bt = function() { console.warn('[initLegacyApp] Bt called before initialization'); };
  let Mt = function() { console.warn('[initLegacyApp] Mt called before initialization'); };
  let Mn = function() { console.warn('[initLegacyApp] Mn (markErrorbornStorySeen) called before initialization'); };
  let loadTransactions = function() { console.warn('[initLegacyApp] loadTransactions called before initialization'); };
  let loadRoyaltyStats = function() { console.warn('[initLegacyApp] loadRoyaltyStats called before initialization'); };
  // Функции рендеринга боевого экрана
  // Простые реализации, которые будут работать до полной инициализации
  // Playback функции теперь используют модуль playback.js
  let po = renderBattlePanels;
  let mo = renderBattleStage;
  let fo = renderBattleProgress;
  let ho = renderBattleLog;
  // Helper функции io, Qe, Ue, Ct, ao теперь берутся из creatureDetailsManager
  
  ((window.showScreen = showScreen),
    (window.claimErrorbornReward = Cn),
    (window.loadErrorbornScreen = rt),
    (window.filterCollection = function(...args) {
      if (DnInitialized && typeof Dn === 'function') {
        return Dn(...args);
      } else {
        console.warn('[initLegacyApp] filterCollection called before initialization');
        setTimeout(() => {
          if (DnInitialized && typeof Dn === 'function') {
            Dn(...args);
          }
        }, 100);
      }
    }),
    (window.sortCollection = function(...args) {
      if (NnInitialized && typeof Nn === 'function') {
        return Nn(...args);
      } else {
        console.warn('[initLegacyApp] sortCollection called before initialization');
        setTimeout(() => {
          if (NnInitialized && typeof Nn === 'function') {
            Nn(...args);
          }
        }, 100);
      }
    }),
    (window.runBattleSimulation = function(...args) {
      if (OsInitialized && typeof Os === 'function') {
        return Os(...args);
      } else {
        console.warn('[initLegacyApp] runBattleSimulation called before initialization');
      }
    }),
    (window.showSendGiftScreen = function(...args) {
      if (bsInitialized && typeof bs === 'function') {
        return bs(...args);
      } else {
        console.warn('[initLegacyApp] showSendGiftScreen called before initialization');
        setTimeout(() => {
          if (bsInitialized && typeof bs === 'function') {
            bs(...args);
          }
        }, 100);
      }
    }),
    (window.sendGift = function(...args) {
      if (wsInitialized && typeof ws === 'function') {
        return ws(...args);
      } else {
        console.warn('[initLegacyApp] sendGift called before initialization');
        setTimeout(() => {
          if (wsInitialized && typeof ws === 'function') {
            ws(...args);
          }
        }, 100);
      }
    }),
    (window.showIncomingGifts = function(...args) {
      if (DeInitialized && typeof De === 'function') {
        return De(...args);
      } else {
        console.warn('[initLegacyApp] showIncomingGifts called before initialization');
        setTimeout(() => {
          if (DeInitialized && typeof De === 'function') {
            De(...args);
          }
        }, 100);
      }
    }),
    (window.showSentGifts = function(...args) {
      if (XtInitialized && typeof Xt === 'function') {
        return Xt(...args);
      } else {
        console.warn('[initLegacyApp] showSentGifts called before initialization');
        setTimeout(() => {
          if (XtInitialized && typeof Xt === 'function') {
            Xt(...args);
          }
        }, 100);
      }
    }),
    (window.acceptGift = function(...args) {
      if ($sInitialized && typeof $s === 'function') {
        return $s(...args);
      } else {
        console.warn('[initLegacyApp] acceptGift called before initialization');
        setTimeout(() => {
          if ($sInitialized && typeof $s === 'function') {
            $s(...args);
          }
        }, 100);
      }
    }),
    (window.declineGift = function(...args) {
      if (_sInitialized && typeof _s === 'function') {
        return _s(...args);
      } else {
        console.warn('[initLegacyApp] declineGift called before initialization');
        setTimeout(() => {
          if (_sInitialized && typeof _s === 'function') {
            _s(...args);
          }
        }, 100);
      }
    }),
    (window.claimDaily = function(...args) {
      if (ksInitialized && typeof ks === 'function') {
        return ks(...args);
      } else {
        console.warn('[initLegacyApp] claimDaily called before initialization');
      }
    }),
    (window.buyPackage = As),
    (window.showProfessions = function(...args) {
      if (SsInitialized && typeof Ss === 'function') {
        return Ss(...args);
      } else {
        console.warn('[initLegacyApp] window.showProfessions called before initialization, args:', args);
        // Пробуем вызвать с задержкой, если функция еще не инициализирована
        setTimeout(() => {
          if (SsInitialized && typeof Ss === 'function') {
            Ss(...args);
          }
        }, 100);
      }
    }),
    (window.assignProfession = function(...args) {
      if (TsInitialized && typeof Ts === 'function') {
        return Ts(...args);
      } else {
        console.warn('[initLegacyApp] window.assignProfession called before initialization, args:', args);
        // Пробуем вызвать с задержкой, если функция еще не инициализирована
        setTimeout(() => {
          if (TsInitialized && typeof Ts === 'function') {
            Ts(...args);
          }
        }, 100);
      }
    }),
    (window.showUpgrades = function(...args) {
      if (tnInitialized && typeof tn === 'function') {
        return tn(...args);
      } else {
        console.warn('[initLegacyApp] showUpgrades called before initialization');
      }
    }),
    (window.applyUpgrade = Ls),
    (window.loadBattleHistory = function(...args) {
      if (YeInitialized && typeof Ye === 'function') {
        return Ye(...args);
      } else {
        console.warn('[initLegacyApp] loadBattleHistory called before initialization');
      }
    }),
    (window.loadPublicBattles = Ke),
    (window.viewBattleDetail = Ws));
  // Errorborn status manager
  const errorbornStatusManager = createErrorbornStatusManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    getErrorbornStatus: () => S,
    setErrorbornStatus: (status) => {
      S = status;
    },
    getErrorbornStatusTimestamp: () => I,
    setErrorbornStatusTimestamp: (timestamp) => {
      I = timestamp;
    },
    renderErrorbornQuestCard: () => {
      // Используем renderErrorbornQuestCard из модуля
      at(S);
    },
    renderErrorbornScreen: function(...args) {
      if (qeInitialized && typeof qe === 'function') {
        return qe(...args);
      } else {
        console.warn('[initLegacyApp] renderErrorbornScreen called before initialization');
      }
    },
    loadHomeScreenData: $e,
  });

  const {
    fetchErrorbornStatus: Ee,
    loadErrorbornScreen: errorbornLoadScreen,
    claimErrorbornReward: errorbornClaimReward,
    markErrorbornStorySeen: errorbornMarkStorySeen,
  } = errorbornStatusManager;
  
  // Обновляем переменные реальными функциями
  rt = errorbornLoadScreen;
  Cn = errorbornClaimReward;
  Mn = errorbornMarkStorySeen;

  // Errorborn quest renderer
  const errorbornQuestRenderer = createErrorbornQuestRenderer({
    escapeHtml: k,
  });

  const { renderErrorbornQuestCard: errorbornQuestCard, renderErrorbornScreen: errorbornScreen } =
    errorbornQuestRenderer;
  
  // Обновляем переменные реальными функциями
  at = errorbornQuestCard;
  qe = errorbornScreen;
  qeInitialized = true;
  // Функции rt, Cn, Mn теперь берутся из errorbornStatusManager
  // Home screen loader
  // Используем безопасные обертки для Hn и dn, так как они могут быть еще не инициализированы
  const homeScreenLoader = createHomeScreenLoader({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    getCurrentUser: () => d,
    setCurrentUser: (user) => {
      d = user;
    },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
    fetchProfessionsData,
    renderCollectionPreview: (collection) => {
      // Безопасный вызов Hn
      if (HnInitialized && typeof Hn === 'function') {
        Hn(collection);
      } else {
        console.warn('[initLegacyApp] Hn not yet initialized, skipping renderCollectionPreview');
      }
    },
    loadAchievementsStats: () => {
      // Безопасный вызов dn
      if (dnInitialized && typeof dn === 'function') {
        dn();
      } else {
        console.warn('[initLegacyApp] dn not yet initialized, skipping loadAchievementsStats');
      }
    },
    fetchErrorbornStatus: Ee,
    checkAdminStatus,
  });

  // Глобальная функция для обновления существа на главном экране
  window.setHomeScreenCreature = function(creature) {
    try {
      if (!creature) {
        console.warn('[setHomeScreenCreature] Called with empty creature');
        return;
      }

      // Сохраняем override, чтобы последующие обновления UI использовали это существо
      window.__homeScreenCreatureOverride = { ...creature };

      console.log('[setHomeScreenCreature] Updating home screen creature:', {
        id: creature.id || creature.userCreatureId || creature.creatureId || creature.creature_id,
        name: creature.name,
      });

      const doc = document;

      const characterName = doc.getElementById('glitchlings-character-name');
      if (characterName) {
        characterName.textContent = (creature.name || 'BITCOR').toUpperCase();
      }

      const characterLevel = doc.getElementById('glitchlings-character-level-value');
      if (characterLevel) {
        characterLevel.textContent = creature.level || 1;
      }

      const characterImage = doc.getElementById('glitchlings-character-image');
      const characterEmoji = doc.getElementById('glitchlings-character-emoji');

      const creatureId =
        creature.creatureId ||
        creature.creature_id ||
        creature.id ||
        creature.userCreatureId ||
        1;
      const imageUrl =
        creature.imageUrl ||
        creature.image_url ||
        `/creatures/${creatureId}.png`;

      if (characterImage && characterEmoji) {
        characterImage.src = imageUrl;
        characterImage.alt = creature.name || 'Glitchling';
        characterImage.style.display = 'block';
        characterImage.style.height = 'auto';
        characterImage.style.width = '';
        characterEmoji.style.display = 'none';
      }
    } catch (error) {
      console.warn('[setHomeScreenCreature] Failed to update home creature:', error);
    }
  };

  // Переопределяем $e (уже объявлен как var выше для hoisting)
  $e = homeScreenLoader.loadHomeScreenData;
  const loadOnlineUsersCount = homeScreenLoader.loadOnlineUsersCount;
  
  // Менеджер кнопки админ-панели
  const adminButtonManager = createAdminButtonManager({
    getCurrentUser: () => d,
    getTelegramWebApp: getTelegramWebAppSafe,
    showScreen: (screenName) => {
      if (typeof showScreen === 'function') {
        showScreen(screenName);
      } else if (window.showScreen) {
        window.showScreen(screenName);
      }
    },
    checkAdminStatus: checkAdminStatus // Используем единую проверку через API
  });
  
  // Экспортируем checkAdminStatus в window для использования в userData.js
  window.checkAdminStatus = checkAdminStatus;
  
  // Вызываем проверку кнопки после загрузки главного экрана
  let onlineUsersUpdateInterval = null;
  const originalLoadHomeScreenData = $e;
  $e = async function() {
    await originalLoadHomeScreenData();
    // Применяем случайный фон к главному экрану
    applyRandomBackgroundToHome();
    // Проверяем кнопку после загрузки данных
    adminButtonManager.checkAndShow();
    // Обновляем количество онлайн пользователей каждые 30 секунд
    if (typeof loadOnlineUsersCount === 'function') {
      // Очищаем предыдущий интервал, если он существует
      if (onlineUsersUpdateInterval) {
        clearInterval(onlineUsersUpdateInterval);
      }
      // Запускаем обновление сразу
      loadOnlineUsersCount();
      // Устанавливаем периодическое обновление
      onlineUsersUpdateInterval = setInterval(() => {
        loadOnlineUsersCount();
      }, 30000); // Обновляем каждые 30 секунд
    }
  };
  
  // Также проверяем при показе главного экрана
  const originalShowScreen = showScreen;
  showScreen = function(screenName) {
    originalShowScreen(screenName);
    // Применяем фон на всех экранах
    setTimeout(() => {
      applyRandomBackgroundToHome();
    }, 100);
    
    if (screenName === 'home' || screenName === '') {
      setTimeout(() => adminButtonManager.ensureButton(), 100);
      setTimeout(() => adminButtonManager.ensureButton(), 500);
    }
  };

  // Функции Hn, zn, qt теперь берутся из creatureCardsRenderer
  // Переопределяем Hn реальной функцией (Hn уже объявлена выше)
  Hn = function(e) {
    const t = document.getElementById("collection-preview");
    if (!t) return;
    t.innerHTML = "";
    if (e.length === 0) {
      const s = document.createElement("div");
      s.className = "collection-preview-empty";
      s.innerHTML = `У вас пока нет существ. Зайдите в <button class="btn-link" onclick="showScreen('store')">магазин</button> и приобретите первое существо!`;
      t.appendChild(s);
      return;
    }
    const n = e.slice(0, 6);
    n.forEach((s) => {
      const o = zn(s);
      o.onclick = () => ft(s);
      t.appendChild(o);
    });
    if (e.length > n.length) {
      const s = document.createElement("div");
      s.className = "creature-card--preview more";
      s.innerHTML = `<div style="font-size: 32px;">➕</div><div class="creature-meta">Ещё ${e.length - n.length}</div>`;
      s.onclick = () => showScreen("collection");
      t.appendChild(s);
    }
  };
  HnInitialized = true;
  // Старые функции lt, dt, ut, Dn, Nn заменены на модули выше
  // Используем функцию из модуля
  /**
   * Активирует фильтр "Все" в коллекции
   * 
   * Обертка над activateAllFilter для обратной совместимости.
   * 
   * @returns {void}
   */
  function Ut() {
    activateAllFilter();
  }

  // Временная переменная для хранения функции показа деталей существа из магазина
  let On = null;
  
  // Объект-обертка для функции показа деталей, чтобы можно было обновлять ссылку
  const showCreatureDetailsFromStoreRef = {
    fn: null
  };

  // Store screen loader (создаём после определения creatureCardsRenderer, но до creaturePurchaseManager)
  // Используем объект-обертку для доступа к функции после её инициализации
  const storeScreenLoader = createStoreScreenLoader({
    safeFetch: safeFetchWithEnhancements,
    getApiUrl: () => apiUrl,
    renderCreatureCard: (creature) => {
      const card = qt(creature, true);
      // Используем функцию из объекта-обертки, которая будет обновлена после создания creaturePurchaseManager
      card.onclick = () => {
        if (showCreatureDetailsFromStoreRef.fn && typeof showCreatureDetailsFromStoreRef.fn === 'function') {
          console.log('[Store] Opening creature details:', creature.id || creature.creatureId);
          showCreatureDetailsFromStoreRef.fn(creature);
        } else {
          console.error('[Store] showCreatureDetailsFromStore function not available', {
            hasFn: !!showCreatureDetailsFromStoreRef.fn,
            type: typeof showCreatureDetailsFromStoreRef.fn
          });
          // Fallback: попробуем использовать On
          if (On && typeof On === 'function') {
            On(creature);
          } else {
            const telegramWebApp = getTelegramWebAppSafe();
            if (telegramWebApp?.showAlert) {
              telegramWebApp.showAlert('Ошибка: функция открытия деталей не инициализирована. Попробуйте перезагрузить приложение.');
            }
          }
        }
      };
      return card;
    },
  });

  const { loadStoreScreen: lt } = storeScreenLoader;

  // Creature purchase manager
  const creaturePurchaseManager = createCreaturePurchaseManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    getCurrentUser: () => d,
    setCurrentUser: (user) => {
      d = user;
    },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
    showScreen,
    showCreatureDetails: null, // Будет установлено после создания creatureDetailsManager
    loadStoreScreen: lt,
    showPaymentScreen: Zt,
    escapeHtmlFn: k,
  });

  const {
    buyCreature: Rn,
    showBuyConfirm: jn,
    showCreatureDetailsFromStore: showCreatureDetailsFromStoreFn,
    setShowCreatureDetails: setShowCreatureDetailsFn,
    showConfirm: pt,
  } = creaturePurchaseManager;

  // Обновляем обе ссылки для совместимости
  On = showCreatureDetailsFromStoreFn;
  showCreatureDetailsFromStoreRef.fn = showCreatureDetailsFromStoreFn;
  
  console.log('[Store] showCreatureDetailsFromStore initialized:', typeof showCreatureDetailsFromStoreFn);

  // Creature details manager
  const creatureDetailsManager = createCreatureDetailsManager({
    escapeHtml: k,
    typeNames: TYPE_NAMES,
    upgradeMethods: M,
    formatStatName,
    getProfessionMeta,
    getProfessionIcon,
    getProfessionName,
    safeFetchWithEnhancements,
    apiUrl,
    buildAuthHeaders,
    getTelegramWebAppSafe,
    fetchProfessionsData,
    purchaseCreature: jn, // showBuyConfirm - показывает подтверждение перед покупкой
    showProfessions: (creatureId) => {
      if (SsInitialized && typeof Ss === 'function') {
        Ss(creatureId);
      }
    },
    showUpgrades: (creatureId) => {
      if (tnInitialized && typeof tn === 'function') {
        tn(creatureId);
      }
    },
    showUpgradeHistory: (creatureId) => {
      if (typeof Is === 'function') {
        Is(creatureId);
      }
    },
  });

  const {
    loadCreatureDetails: ft,
    renderCreatureDetails: Ft,
  } = creatureDetailsManager;
  
  // Экспортируем функции для глобального использования
  window.loadCreatureDetails = ft;
  window.renderCreatureDetails = Ft;

  // Обновляем showCreatureDetails в creaturePurchaseManager через специальную функцию
  if (setShowCreatureDetailsFn && typeof setShowCreatureDetailsFn === 'function') {
    setShowCreatureDetailsFn((creature, availableUpgradeMethods) => {
      Ft(creature, availableUpgradeMethods);
    });
  } else {
    // Fallback на старый способ (для совместимости)
    creaturePurchaseManager.showCreatureDetails = (creature, availableUpgradeMethods) => {
      Ft(creature, availableUpgradeMethods);
    };
  }

  // Collection screen manager (создаём после определения creatureCardsRenderer)
  const collectionScreenManager = createCollectionScreenManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    getCurrentUser: () => d,
    setCurrentUser: (user) => {
      d = user;
    },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
    fetchProfessionsData,
    renderCreatureCard: (creature) => {
      const card = qt(creature, false);
      card.onclick = () => ft(creature);
      return card;
    },
  });

  const {
    loadCollectionScreen: dt,
    filterCollection: collectionFilter,
    sortCollection: collectionSort,
    renderCollection: ut,
  } = collectionScreenManager;
  
  // Обновляем переменные реальными функциями
  Dn = collectionFilter;
  DnInitialized = true;
  Nn = collectionSort;
  NnInitialized = true;

  const userAutocompleteManager = createUserAutocompleteManager({
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    escapeHtmlFn: k,
  });

  // Gifts screen manager (передаём функции напрямую из менеджера, чтобы избежать TDZ)
  const giftsScreenManager = createGiftsScreenManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    fetchProfessionsData,
    renderProfessionBadge,
    showScreen,
    escapeHtmlFn: k,
    initUserAutocomplete: userAutocompleteManager.initUserAutocomplete,
    hideUserAutocomplete: userAutocompleteManager.hideUserAutocomplete,
    getCurrentUser: () => d,
    setCurrentUser: (user) => {
      d = user;
    },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
  });

  const {
    initUserAutocomplete: fs,
    searchUsers: hs,
  } = userAutocompleteManager;

  const {
    showSendGiftScreen: giftsShowSend,
    sendGift: giftsSend,
    loadIncomingGifts: giftsLoadIncoming,
    loadSentGifts: giftsLoadSent,
    acceptGift: giftsAccept,
    declineGift: giftsDecline,
  } = giftsScreenManager;
  
  // Обновляем переменные реальными функциями
  bs = giftsShowSend;
  bsInitialized = true;
  ws = giftsSend;
  wsInitialized = true;
  De = giftsLoadIncoming;
  DeInitialized = true;
  Xt = giftsLoadSent;
  XtInitialized = true;
  $s = giftsAccept;
  $sInitialized = true;
  _s = giftsDecline;
  _sInitialized = true;

  // Payment screen manager
  const paymentScreenManager = createPaymentScreenManager({
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    showScreen,
  });

  const {
    loadPaymentPackages: kt,
    showPaymentScreen: paymentShowScreen,
    buyPackage: paymentBuyPackage,
  } = paymentScreenManager;

  // Progress Pass manager
  const progressPassManager = createProgressPassManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getCurrentUser: () => d,
    setCurrentUser: (user) => { d = user; },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
  });

  const {
    loadProgressScreen: loadProgressScreenFn,
    claimTier: claimTierFn,
    switchTab: switchProgressTabFn,
  } = progressPassManager;

  // Экспортируем функции глобально
  window.loadProgressScreen = loadProgressScreenFn;
  window.claimProgressTier = claimTierFn;
  window.switchProgressTab = switchProgressTabFn;

  // Обновляем функцию showPassTab
  window.showPassTab = function(tabName) {
    switchProgressTabFn(tabName);
  };
  
  // Обновляем переменные реальными функциями
  Zt = paymentShowScreen;
  As = paymentBuyPackage;
  
  // Экспортируем buyPackage глобально для использования в onclick
  window.buyPackage = paymentBuyPackage;
  
  // Buy resource function
  window.buyResource = async function(resourceId) {
    const telegramWebApp = getTelegramWebAppSafe();
    const authHeaders = buildAuthHeaders();
    
    if (!authHeaders) {
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert('Ошибка авторизации. Перезагрузите приложение.');
      }
      return;
    }
    
    try {
      const apiUrl = resolveApiUrl();
      const response = await safeFetchWithEnhancements(`${apiUrl}/shop/buy-resource`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ resourceId })
      });
      
      if (response.success && response.data) {
        // Обновляем баланс пользователя
        if (d && response.data.user) {
          d.coins = response.data.user.coins;
          d.stars = response.data.user.stars;
          updateUserBalancesUIWrapper();
        }
        
        // Показываем сообщение об успехе
        const resourceNames = {
          'power-10': 'Энергия x10',
          'power-30': 'Энергия x30',
          'coins-100': 'Монеты x100',
          'coins-500': 'Монеты x500'
        };
        
        const resourceName = resourceNames[resourceId] || 'Ресурс';
        const resourceType = response.data.resourceType;
        const amount = response.data.resourceAmount;
        
        // Показываем кастомное уведомление
        const lootboxManager = window.lootboxManager;
        if (lootboxManager && lootboxManager.showLootboxNotification) {
          if (resourceType === 'energy') {
            lootboxManager.showLootboxNotification('⚡ Энергия получена', `+${amount} энергии добавлено в пул\nРаспределите через экран "⚡ Энергия"`);
            // Обновляем экран энергии, если он открыт
            setTimeout(() => {
              if (typeof window.loadEnergyManager === 'function') {
                window.loadEnergyManager();
              }
            }, 500);
          } else if (resourceType === 'coins') {
            lootboxManager.showLootboxNotification('💰 Монеты получены', `+${amount} монет`);
          } else {
            lootboxManager.showLootboxNotification('✅ Получено', resourceName);
          }
        } else if (telegramWebApp?.showAlert) {
          let message = `✅ Получено: ${resourceName}`;
          if (resourceType === 'energy') {
            message = `✅ Получено: ⚡ ${amount} энергии добавлено в пул\nРаспределите через экран "⚡ Энергия"`;
            // Обновляем экран энергии, если он открыт
            setTimeout(() => {
              if (typeof window.loadEnergyManager === 'function') {
                window.loadEnergyManager();
              }
            }, 500);
          } else if (resourceType === 'coins') {
            message = `✅ Получено: 💰 ${amount} монет`;
          }
          telegramWebApp.showAlert(message);
        }
      } else {
        // Обрабатываем ошибки
        const errorData = response.data || response.payload || {};
        const errorCode = errorData.error || response.error;
        let errorMessage = errorData.message || response.message || 'Ошибка покупки ресурса';
        
        // Обрабатываем ошибки недостатка валюты
        if (errorCode === 'insufficient_coins' || errorCode === 'insufficient_stars') {
          const currency = errorData.currency || (errorCode === 'insufficient_coins' ? 'coins' : 'stars');
          const currencyIcon = currency === 'coins' ? '💰' : '⭐';
          const currencyName = currency === 'coins' ? 'монет' : 'звёзд';
          const required = errorData.required || 0;
          const current = errorData.current || 0;
          const missing = errorData.missing || (required - current);
          
          // Используем сообщение с бэкенда или формируем своё
          if (errorData.message) {
            errorMessage = errorData.message;
          } else {
            errorMessage = `Недостаточно ${currencyName}.\nТребуется: ${required} ${currencyIcon}\nУ вас: ${current} ${currencyIcon}\nНе хватает: ${missing} ${currencyIcon}`;
          }
          
          // Предлагаем пополнить баланс
          const confirmed = await new Promise((resolve) => {
            if (telegramWebApp && typeof telegramWebApp.showConfirm === 'function') {
              try {
                telegramWebApp.showConfirm(
                  `${errorMessage}\n\nХотите пополнить баланс?`,
                  (result) => resolve(result === true)
                );
              } catch (error) {
                console.warn('[buyResource] showConfirm error:', error);
                resolve(window.confirm ? window.confirm(`${errorMessage}\n\nХотите пополнить баланс?`) : true);
              }
            } else {
              resolve(window.confirm ? window.confirm(`${errorMessage}\n\nХотите пополнить баланс?`) : true);
            }
          });
          
          if (confirmed) {
            // Переходим на экран покупки
            if (typeof window.showPaymentScreen === 'function') {
              window.showPaymentScreen();
            } else if (typeof window.showScreen === 'function') {
              window.showScreen('payment');
            } else {
              const lootboxManager = window.lootboxManager;
              if (lootboxManager && lootboxManager.showLootboxNotification) {
                lootboxManager.showLootboxNotification('💳 Пополнение баланса', 'Перейдите в раздел "Платежи" для пополнения баланса');
              } else if (telegramWebApp?.showAlert) {
                telegramWebApp.showAlert('Перейдите в раздел "Платежи" для пополнения баланса');
              }
            }
          } else {
            // Показываем сообщение об ошибке
            const lootboxManager = window.lootboxManager;
            if (lootboxManager && lootboxManager.showLootboxNotification) {
              lootboxManager.showLootboxNotification('❌ Недостаточно средств', errorMessage);
            } else if (telegramWebApp?.showAlert) {
              telegramWebApp.showAlert(errorMessage);
            }
          }
        } else {
          // Для других ошибок просто показываем сообщение
          const lootboxManager = window.lootboxManager;
          if (lootboxManager && lootboxManager.showLootboxNotification) {
            lootboxManager.showLootboxNotification('❌ Ошибка', errorMessage);
          } else if (telegramWebApp?.showAlert) {
            telegramWebApp.showAlert(errorMessage);
          }
        }
      }
    } catch (error) {
      console.error('[buyResource] Ошибка:', error);
      const lootboxManager = window.lootboxManager;
      if (lootboxManager && lootboxManager.showLootboxNotification) {
        lootboxManager.showLootboxNotification('❌ Ошибка', 'Ошибка покупки ресурса. Попробуйте позже.');
      } else if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert('Ошибка покупки ресурса. Попробуйте позже.');
      }
    }
  };

  // Professions and upgrades manager
  const professionsUpgradesManager = createProfessionsUpgradesManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    getCurrentUser: () => d,
    setCurrentUser: (user) => {
      d = user;
    },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
    fetchProfessionsData,
    getProfessionStatLabel,
    getUpgradeMethods: () => M,
    showScreen,
  });

  const {
    showProfessions: professionsShow,
    assignProfession: professionsAssign,
    showUpgrades: upgradesShow,
    applyUpgrade: upgradesApply,
    showUpgradeHistory: Is,
  } = professionsUpgradesManager;
  
  // Обновляем переменные реальными функциями
  Ss = professionsShow;
  SsInitialized = true;
  Ts = professionsAssign;
  TsInitialized = true;
  tn = upgradesShow;
  tnInitialized = true;
  Ls = upgradesApply;
  window.applyUpgrade = Ls; // Обновляем window.applyUpgrade после инициализации

  // Creature cards renderer
  const creatureCardsRenderer = createCreatureCardsRenderer({
    escapeHtml: k,
    renderProfessionBadge,
  });

  const { renderCollectionPreviewCard: zn, renderCreatureCard: qt } =
    creatureCardsRenderer;

  // Функции pt, Rn, jn, On теперь берутся из creaturePurchaseManager
  // Функции ft, Ft теперь берутся из creatureDetailsManager
  // Старый код удален
  
  // Используем функции из модуля
  /**
   * Показывает или скрывает баннер победителя боя
   * 
   * Обертка над showBattleWinnerBanner/hideBattleWinnerBanner для обратной совместимости.
   * 
   * @param {string|null|undefined} e - Имя победителя или null/undefined для скрытия
   * @returns {void}
   */
  function Un(e) {
    if (!e) {
      hideBattleWinnerBanner();
      return;
    }
    showBattleWinnerBanner(e);
  }
  // Функции Be, ht, Ge, Jt, Me, ue, fe, qn, Fn, We, Gn, _e, Wn, Pe теперь берутся из battleViewManager
  // Используем функцию из модуля
  let Be = hideBattleWinnerBanner;
  // ht, Ge, Me, ue, fe, Jt, qn, Fn, We, Gn, _e, Wn, Pe будут определены ниже после создания battleViewManager
  let ht;
  let Ge;
  let Me;
  let ue;
  let fe;
  let Jt;
  let qn;
  let Fn;
  let We;
  let Gn;
  let _e;
  // Wn объявлен выше
  let Pe;
  // Функции gt, bt, Vn, Jn теперь берутся из battleAnimationsManager
  // Создаем заглушки, которые будут заменены после создания менеджера
  let gt = function(creatureId, animationType, duration = 600) {
    if (!creatureId) return;
    const panel = getPanelById(w.panels, creatureId);
    if (!panel) return;
    const element = document.querySelector(`[data-panel-id="${creatureId}"]`);
    if (!element) return;
    const className = `battle-animate-${animationType}`;
    element.classList.add(className);
    setTimeout(() => {
      element.classList.remove(className);
    }, duration);
  };
  let bt = function(teamId, animationType, duration = 600) {
    if (!teamId) return;
    const elements = document.querySelectorAll(`[data-team="${teamId}"], .battle-team-${teamId}`);
    if (!elements || elements.length === 0) return;
    const className = `battle-animate-team-${animationType}`;
    elements.forEach(element => {
      element.classList.add(className);
      setTimeout(() => {
        element.classList.remove(className);
      }, duration);
    });
  };
  let Vn = function(creatureId, damage, isCrit = false) {
    if (!creatureId || !damage) return;
    const element = document.querySelector(`[data-panel-id="${creatureId}"]`);
    if (!element) return;
    const damageElement = document.createElement('div');
    damageElement.className = `battle-damage ${isCrit ? 'battle-damage--crit' : ''}`;
    damageElement.textContent = isCrit ? `💥 ${damage}` : damage;
    damageElement.style.cssText = `
      position: absolute;
      color: ${isCrit ? '#ff0000' : '#ff6b6b'};
      font-weight: 700;
      font-size: 18px;
      pointer-events: none;
      z-index: 1000;
      animation: damageFloat 1s ease-out forwards;
    `;
    element.style.position = 'relative';
    element.appendChild(damageElement);
    setTimeout(() => {
      if (damageElement.parentNode) {
        damageElement.parentNode.removeChild(damageElement);
      }
    }, 1000);
  };
  let Jn = function(teamId, damage, isCrit = false) {
    if (!teamId || !damage) return;
    const elements = document.querySelectorAll(`[data-team="${teamId}"], .battle-team-${teamId}`);
    if (!elements || elements.length === 0) return;
    elements.forEach(element => {
      const damageElement = document.createElement('div');
      damageElement.className = `battle-damage ${isCrit ? 'battle-damage--crit' : ''}`;
      damageElement.textContent = isCrit ? `💥 ${damage}` : damage;
      damageElement.style.cssText = `
        position: absolute;
        color: ${isCrit ? '#ff0000' : '#ff6b6b'};
        font-weight: 700;
        font-size: 18px;
        pointer-events: none;
        z-index: 1000;
        animation: damageFloat 1s ease-out forwards;
      `;
      element.style.position = 'relative';
      element.appendChild(damageElement);
      setTimeout(() => {
        if (damageElement.parentNode) {
          damageElement.parentNode.removeChild(damageElement);
        }
      }, 1000);
    });
  };
  
  // Функции Ce, Ve, vt, Yn, Gt, Kn, Wt, Vt, yt, Xn, Qn теперь берутся из battlePlaybackManager (определены ниже)
  let Ce;
  let Ve;
  let vt;
  let Yn;
  let Gt;
  let Kn;
  let Wt;
  let Vt;
  let yt;
  let Xn;
  let Qn;
  
  // Telemetry Processor
  const telemetryProcessor = createTelemetryProcessor({
    getCombatantName: te,
    formatPercent: K,
    processBattleStatusEffects: is,
    processEventStatusEffects: as,
    cleanupExpiredStatusEffects: ss,
    serializeStatusEffects: os,
    formatStatusName: Yt,
  });
  
  // Присваиваем функции из telemetryProcessor
  const rs = (eventName) => telemetryProcessor.formatEventName(eventName);
  const ls = (event, panels) => telemetryProcessor.formatEvent(event, panels);
  const cs = (battleData, panels) => telemetryProcessor.processByRound(battleData, panels);
  const ds = (battleData, timeline) => telemetryProcessor.buildStatusTimeline(battleData, timeline);
  // Используем функцию из модуля
  /**
   * Загружает данные боя
   * 
   * Обертка над loadBattleData для обратной совместимости.
   * Использует безопасный доступ к J и X для избежания проблем с TDZ.
   * 
   * @param {Object} e - Данные боя от сервера
   * @returns {void}
   */
  function wt(e) {
    // Используем безопасный доступ к J и X для избежания проблем с TDZ
    const currentJ = (typeof window !== 'undefined' && window.__glitchlingsJ !== undefined) ? window.__glitchlingsJ : J;
    const currentX = (typeof window !== 'undefined' && window.__glitchlingsX !== undefined) ? window.__glitchlingsX : X;
    
    loadBattleData(e, w, {
      buildBattlePanels,
      buildBattleTimeline,
      buildStatusTimeline: ds,
      processTelemetryByRound: cs,
      determineWinnerInfo,
      getBattleWinner: getBattleWinnerFromModule,
      resetBattleState: Jt,
      clearBattleTimers: Me,
      clearPlaybackTimer: ue,
      hideWinnerBanner: Be,
      renderBattle: fe,
      playerPreview: currentJ,
      enemyPreview: currentX
    });
  }
  
  // Battle modal manager
  const battleModalManager = createBattleModalManager({
    getBattleState: () => w,
    renderBattlePanels: () => po(w),
    renderBattleLog: () => ho(w),
    renderBattleStage: () => mo(w),
    renderBattleProgress: () => fo(w),
    clearPlaybackTimer: ue,
    clearBattleTimers: Me,
    hideWinnerBanner: Be,
    resetBattleState: Jt,
    renderBattle: fe,
  });

  // Заменяем заглушки на реальные функции из менеджера
  const {
    openBattleModal: openModal,
    closeBattleModal: closeModal,
    resetBattleState: resetState,
    clearBattleTimers: clearTimers,
    clearPlaybackTimer: clearTimer,
    hideWinnerBanner: hideBanner,
    renderBattle: render,
  } = battleModalManager;
  
  // Переопределяем переменные
  ht = openModal;
  Ge = closeModal;
  Jt = resetState;
  Me = clearTimers;
  ue = clearTimer;
  Be = hideBanner;
  fe = render;

  // Battle animations manager
  const battleAnimationsManager = createBattleAnimationsManager({
    getBattleState: () => w,
    getPanelById,
  });

  // Battle enhancements manager (улучшенные анимации)
  let battleEnhancementsManager;
  try {
    battleEnhancementsManager = createBattleEnhancementsManager({
      getBattleState: () => w,
      getPanelById,
    });
  } catch (error) {
    console.warn('[battle] Failed to create enhancements manager:', error);
    battleEnhancementsManager = null;
  }

  // Заменяем заглушки на реальные функции из менеджера
  const {
    animateCreature: animateCreatureFn,
    animateTeam: animateTeamFn,
    showCreatureDamage: showCreatureDamageFn,
    showTeamDamage: showTeamDamageFn,
  } = battleAnimationsManager;
  
  // Переопределяем переменные
  gt = animateCreatureFn;
  bt = animateTeamFn;
  
  // Используем улучшенные анимации урона
  Vn = function(creatureId, damage, isCrit = false) {
    // Используем улучшенную версию если доступна
    if (battleEnhancementsManager) {
      try {
        battleEnhancementsManager.showDamage(creatureId, damage, isCrit);
      } catch (error) {
        console.warn('[battle] Enhanced damage animation failed:', error);
      }
    }
    // Также вызываем стандартную для совместимости
    showCreatureDamageFn(creatureId, damage, isCrit);
  };
  
  Jn = showTeamDamageFn;

  // Battle Playback Manager и Battle Controls Manager будут созданы после инициализации _e, Wn, Pe (см. ниже)

  let He = null; // Выбранное существо для подарка
  // Функции fs, hs, he теперь берутся из userAutocompleteManager
  // Функции bs, ws, De, Xt теперь берутся из giftsScreenManager
  
  // Gift Helpers
  const giftHelpers = createGiftHelpers({
    escapeHtml: k,
    renderProfessionBadge,
    getSelectedCreature: () => He,
    setSelectedCreature: (creatureId) => { He = creatureId; },
  });
  
  // Присваиваем функции из giftHelpers
  const vs = giftHelpers.createGiftCard;
  const ys = giftHelpers.selectCreature;
  const Qt = giftHelpers.createGiftItemWithButtons;
  // Функции $s, _s теперь берутся из giftsScreenManager
  // Функция ks теперь берётся из dailyLoginManager
  // Функции loadTransactions и loadRoyaltyStats теперь берутся из transactionsManager
  window.loadTransactions = loadTransactions;
  window.loadRoyaltyStats = loadRoyaltyStats;
  // Старые функции loadTransactions и loadRoyaltyStats удалены, теперь используются из модуля
  // Функция en теперь берётся из formatters
  const en = updateWeeklyRewards;
  // Функции Ss, Ts, tn, Ls, Is теперь берутся из professionsUpgradesManager
  // Используем formatStatName из модуля
  const Ze = formatStatName;
  window.showUpgradeHistory = Is;

  // Achievements manager
  const achievementsManager = createAchievementsManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    showScreen,
    escapeHtmlFn: k,
  });

  const {
    fetchAchievements: sn,
    loadAchievements: Pt,
    showAchievements: Vs,
    loadAchievementsStats: achievementsLoadStats,
    getCurrentFilter: getAchievementsCurrentFilter,
  } = achievementsManager;
  
  // Обновляем переменные реальными функциями
  showAchievements = Vs;
  showAchievementsInitialized = true;
  dn = achievementsLoadStats;
  dnInitialized = true;
  
  // Переменная для хранения текущего фильтра достижений (используется в screenHandlers)
  // Обновляется через функцию getAchievementsCurrentFilter
  let At = "all";
  
  // Обновляем At при изменении фильтра через showAchievements
  const originalShowAchievements = Vs;
  showAchievements = function(filter = "all") {
    At = filter;
    originalShowAchievements(filter);
  };

  // Collection data ensurer
  const ensureCollectionData = createCollectionDataEnsurer({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getCollection: () => Y,
    setCollection: (collection) => {
      Y = collection;
    },
    getCurrentUser: () => d,
    setCurrentUser: (user) => {
      d = user;
    },
  });

  // Daily login manager
  const dailyLoginManager = createDailyLoginManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    getCurrentUser: () => d,
    setCurrentUser: (user) => {
      d = user;
    },
    updateUserBalancesUI: updateUserBalancesUIWrapper,
  });

  const { claimDaily: dailyClaim } = dailyLoginManager;
  
  // Обновляем переменную реальной функцией
  ks = dailyClaim;
  ksInitialized = true;
  // Обновляем window.claimDaily
  window.claimDaily = ks;

  // Transactions manager
  const transactionsManager = createTransactionsManager({
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    escapeHtmlFn: k,
  });

  const { loadTransactions: transactionsLoad, loadRoyaltyStats: royaltyStatsLoad } = transactionsManager;
  
  // Обновляем переменные реальными функциями
  loadTransactions = transactionsLoad;
  loadRoyaltyStats = royaltyStatsLoad;

  // Используем функции из helpers
  const St = normalizeString;
  const Ps = (creatureId, userCreatureId) =>
    findCreatureInCollection(Y, creatureId, userCreatureId);
  const Bs = ensureCollectionData;
  // Используем функции из модуля preview напрямую
  const Cs = (creature) => formatCreaturePreview(creature, normalizeStats, St, d);
  const Ms = (preset) => formatAiPresetPreview(preset, normalizeStats, St);
  const Hs = (isEnemy = false) => formatRandomCreaturePreview(isEnemy, Y.length, tt, d);
  const on = (selector, side) => {
    if (!selector) return null;
    if (selector === "user:random") return Hs(side === "enemy");
    if (selector.startsWith("user:")) {
      const { creatureId, userCreatureId } = Ne(selector);
      const creature = Ps(creatureId, userCreatureId);
      return Cs(creature);
    }
    const preset = Lt(selector);
    return preset ? Ms(preset) : null;
  };
  const Ds = (stats, critChance, statLimits) => formatPreviewStatsHtml(stats, critChance, statLimits);
  const an = (elementId, preview, side) => {
    renderCreaturePreview(elementId, preview, side, k, Ht, (stats, critChance) => Ds(stats, critChance, je), je);
  };
  // Tt, Ns, rn теперь берутся из battlePreviewManager (определены выше)
  // Переопределяем Lt реальной функцией (Lt уже объявлена выше)
  Lt = function(e) {
    return bn.find((t) => t.id === e) || null;
  }
  // Ne и zs теперь импортируются из модуля
  const Ne = parseUserCreatureId;
  const zs = () => getUserName(d);
  // Os теперь берется из battleSimulationRunner (определен ниже)
  
  // FormationManager будет создан после инициализации Os (см. ниже)
  
  // Используем функцию из модуля
  const Re = determineBattleWinner;
  // Используем функции из модуля
  const Us = extractBattleFlags;
  const qs = formatBattleLogEntry;
  // Переопределяем Bt реальной функцией (Bt уже объявлена выше)
  // Используем функцию из модуля
  Bt = function(e) {
    renderBattleResult({
      battle: e?.battle,
      summary: e?.summary || {},
      evolutionResults: e?.evolutionResults || [],
      battleState: w,
      escapeHtml: k,
      formatBattleLogEntry: qs,
      getBattleWinner: getBattleWinnerFromModule,
      calculateBattleStats: calculateBattleStats,
      formatBattleStats: formatBattleStats,
      renderBattleStats: renderBattleStats,
    });
  }
  // Battle history manager (создаём после определения всех зависимостей)
  const battleHistoryManager = createBattleHistoryManager({
    safeFetch: safeFetchWithEnhancements,
    buildAuthHeaders,
    getApiUrl: () => apiUrl,
    getTelegramWebApp: getTelegramWebAppSafe,
    escapeHtmlFn: k,
    showBattleModal: ht,
    resetBattleView: Ge,
    loadBattleTimeline: wt,
    renderBattleSummary: Bt,
  });

  const {
    loadBattleHistory: battleLoadHistory,
    loadPublicBattles: battleLoadPublic,
    viewBattleDetail: battleViewDetail,
    getBattleWinner: getBattleWinnerFromModule,
  } = battleHistoryManager;
  
  // Обновляем переменные реальными функциями
  Ye = battleLoadHistory;
  YeInitialized = true;
  window.loadBattleHistory = Ye; // Обновляем напрямую
  Ke = battleLoadPublic;
  Ws = battleViewDetail;
  window.viewBattleDetail = Ws; // Обновляем window.viewBattleDetail после инициализации

  // PvP Manager
  const pvpManager = createPvpManager({
    pvpState: H,
    safeFetch: safeFetchWithEnhancements,
    apiUrl,
    buildAuthHeaders,
    getTelegramWebAppSafe,
    loadBattleData: wt,
    showBattleModal: ht,
    renderBattleView: vt,
    renderBattleResult: Bt,
    loadBattleHistory: Ye,
    checkErrorbornStatus: Ee,
    renderErrorbornScreen: qe,
    parseUserCreatureId,
    updatePvpUI: () => {
      // ze будет определен ниже после создания менеджера
      if (typeof ze === 'function') ze();
    },
  });

  // Присваиваем функции из pvpManager
  js = pvpManager.startPvpSearch;
  cn = pvpManager.cancelPvpSearch;
  Et = pvpManager.handlePvpMatch;
  Rs = pvpManager.pollPvpMatch;
  ne = pvpManager.updatePvpStatus;
  ze = pvpManager.updatePvpControls;
  It = pvpManager.stopPvpPolling;
  ln = pvpManager.startPvpPolling;

  // Battle Preview Manager будет создан после инициализации ps и us (см. ниже)

  // Battle View Manager
  const battleViewManager = createBattleViewManager({
    battleState: w,
    hideBattleWinnerBanner: Be,
    renderBattlePanels: po,
    renderBattleStage: mo,
    renderBattleProgress: fo,
    renderBattleLog: ho,
    renderBattlePlaybackPanels: renderBattlePlaybackPanels,
    renderBattlePlaybackStage: renderBattlePlaybackStage,
    renderBattlePlaybackLog: renderBattlePlaybackLog,
    clearBattleRenderCache,
    onBattleRenderComplete: Kn,
  });

  // Присваиваем функции из battleViewManager
  ht = battleViewManager.showBattleModal;
  Ge = battleViewManager.hideBattleModal;
  Me = battleViewManager.clearBattleCountdown;
  ue = battleViewManager.stopBattleTimer;
  fe = battleViewManager.renderBattleView;
  Jt = battleViewManager.resetBattleView;
  qn = battleViewManager.renderPanels;
  Fn = battleViewManager.renderStage;
  We = battleViewManager.renderProgress;
  Gn = battleViewManager.renderLog;
  _e = battleViewManager.renderPlaybackStage;
  Wn = battleViewManager.renderPlaybackPanels;
  Pe = battleViewManager.renderPlaybackLog;

  // Battle Playback Manager (создаём после инициализации _e, Wn, Pe)
  const battlePlaybackManager = createBattlePlaybackManager({
    battleState: w,
    clearBattleCountdown: Me,
    hideBattleWinnerBanner: Be,
    renderBattleView: fe,
    showBattleWinnerBanner: Un,
    renderPlaybackStage: _e,
    renderPlaybackPanels: Wn,
    renderPlaybackLog: Pe,
    renderProgress: We,
    stopBattleTimer: ue,
    getPanelById,
    showCreatureAnimation: gt,
    showTeamAnimation: bt,
    showDamageNumber: Vn,
    showTeamDamageNumber: Jn,
    battleEnhancementsManager,
  });

  // Присваиваем функции из battlePlaybackManager
  vt = battlePlaybackManager.initializeBattleView;
  Gt = battlePlaybackManager.playBattleStep;
  Kn = battlePlaybackManager.renderBattleEffects;
  Wt = battlePlaybackManager.goToStep;
  Vt = battlePlaybackManager.stepBattle;
  yt = battlePlaybackManager.autoPlayNext;
  Xn = battlePlaybackManager.togglePlayback;
  Qn = battlePlaybackManager.setPlaybackSpeed;
  Ve = battlePlaybackManager.showCountdown;
  Yn = battlePlaybackManager.calculateEventDelay;
  Ce = (timeoutId) => {
    battlePlaybackManager.addCountdownTimeout(timeoutId);
  };

  // Battle controls manager (создаём после инициализации функций из battlePlaybackManager)
  const battleControlsManager = createBattleControlsManager({
    togglePlayback: Xn,
    stepBackward: () => Vt(-1),
    stepForward: () => Vt(1),
    seekToStep: (step) => {
      ue();
      w.isPlaying = false;
      Wt(step);
    },
    setPlaybackSpeed: Qn,
    closeBattleModal: Ge,
    getBattleState: () => w,
  });

  const {
    setupBattlePlaybackControls: us,
    setupBattleModalHandlers: ps,
  } = battleControlsManager;

  // Battle Preview Manager (создаём после инициализации ps и us)
  const battlePreviewManager = createBattlePreviewManager({
    ensureCollectionData: Bs,
    formatCreaturePreview: Cs,
    formatAiPresetPreview: Ms,
    formatRandomCreaturePreview: Hs,
    getCreaturePreview: on,
    renderCreaturePreview: an,
    parseUserCreatureId,
    findCreatureInCollection: Ps,
    findAiPreset: Lt,
    updatePvpUI: ze,
    escapeHtml: k,
    initializeBattleHandlers: Ke,
    setupBattleModalHandlers: ps,
    setupBattlePlaybackControls: us,
    loadBattleHistory: Ye,
    checkErrorbornStatus: Ee,
    startPvpSearch: js,
    cancelPvpSearch: cn,
    collection: Y,
    aiTemplates: bn,
    currentUser: d,
    previewState: { player: J, enemy: X },
    setPreviewState: ({ player, enemy }) => {
      J = player;
      X = enemy;
      if (typeof window !== 'undefined') {
        window.__glitchlingsJ = J;
        window.__glitchlingsX = X;
      }
    },
  });

  const {
    updateBattlePreview: Tt,
    initializeBattleSelects: Ns,
    loadBattleScreen: rn,
  } = battlePreviewManager;

  // Battle Simulation Runner
  Os = createBattleSimulationRunner({
    safeFetch: safeFetchWithEnhancements,
    apiUrl,
    buildAuthHeaders,
    getTelegramWebAppSafe,
    loadBattleData: wt,
    showBattleModal: ht,
    renderBattleView: vt,
    renderBattleResult: Bt,
    loadBattleHistory: Ye,
    checkErrorbornStatus: Ee,
    renderErrorbornScreen: qe,
    parseUserCreatureId: Ne,
    getUserName: zs,
    findAiPreset: Lt,
    cancelPvpSearch: cn,
    clearBattleCountdown: Me,
    collection: Y,
    aiTemplates: bn,
    currentUser: d,
    battleState: w,
  });
  OsInitialized = true;

  // Создаём formationManager после инициализации Os
  try {
    window.__formationManager = createFormationManager({
      userCollection: Y,
      safeFetch: safeFetchWithEnhancements,
      apiUrl: apiUrl,
      buildAuthHeaders: buildAuthHeaders,
      getTelegramWebAppSafe: getTelegramWebAppSafe,
      runBattleSimulation: Os,
      currentUser: d
    });
    console.log('[initLegacyApp] FormationManager created');
  } catch (error) {
    console.error('[initLegacyApp] Error creating FormationManager:', error);
  }

  // Функции Pt, Vs, dn теперь берутся из achievementsManager
  window.showAchievements = function(...args) {
    if (showAchievementsInitialized && typeof showAchievements === 'function') {
      return showAchievements(...args);
    } else {
      console.warn('[initLegacyApp] window.showAchievements called before initialization, args:', args);
      setTimeout(() => {
        if (showAchievementsInitialized && typeof showAchievements === 'function') {
          showAchievements(...args);
        }
      }, 100);
    }
  };
  window.loadAchievements = Pt;
  window.loadAchievementsStats = dn;
  // Объявляем it заранее, чтобы избежать проблем с TDZ
  // it будет обновлена после получения scheduleAuthRetry
  let it = function() {
    console.warn('[initLegacyApp] it called before initialization');
  };
  
  // Используем функцию из модуля
  /**
   * Инициализирует обработчики боя
   * 
   * Обертка над initializeBattleHandlers для обратной совместимости.
   * Настраивает обработчики модального окна боя и элементы управления playback.
   * 
   * @returns {void}
   */
  function un() {
    initializeBattleHandlers(ps, us, scheduleAuthRetry);
  }
  
  // Глобальный обработчик ошибок для отладки
  // Глобальные обработчики ошибок теперь настраиваются через errorHandler
  // Старый код удален
  
  // Инициализация при загрузке DOM
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      try {
        un();
      } catch (error) {
        console.error("[INIT ERROR] Error in DOMContentLoaded:", error);
      }
    });
  } else {
    try {
      un();
    } catch (error) {
      console.error("[INIT ERROR] Error in immediate init:", error);
    }
  }
  
  // Инициализация Telegram WebApp при загрузке окна
  // Вызываем ensureTelegramBootstrapped сразу - она сама проверит доступность WebApp
  window.addEventListener("load", () => {
    try {
      console.log("[INIT] Window loaded, calling ensureTelegramBootstrapped");
      ensureTelegramBootstrapped();
    } catch (error) {
      console.error("[INIT ERROR] Error in window load handler:", error);
      // Пробуем еще раз через небольшую задержку
      setTimeout(() => {
        try {
          console.log("[INIT] Retry ensureTelegramBootstrapped after error");
          ensureTelegramBootstrapped();
        } catch (retryError) {
          console.error("[INIT ERROR] Error in retry:", retryError);
        }
      }, 1000);
    }
  });
  
  // Fallback: если WebApp не загрузился, пробуем еще раз через небольшое время
  // Но это не для ожидания инициализации, а для повторной попытки
  setTimeout(() => {
    try {
      if (!d && !D && !getTelegramWebAppSafe()) {
        console.log("[INIT] Fallback: retrying ensureTelegramBootstrapped");
        ensureTelegramBootstrapped();
      }
    } catch (error) {
      console.error("[INIT ERROR] Error in fallback:", error);
    }
  }, 1000);
  
  // Таймаут для автоматического скрытия overlay через 15 секунд (на случай, если авторизация застряла)
  // Это особенно важно для Android/Windows, где могут быть проблемы с инициализацией
  setTimeout(() => {
    if (!D) {
      // Если авторизация еще не завершена через 15 секунд, скрываем overlay и показываем приложение
      console.warn("[INIT] Auth timeout after 15 seconds - hiding overlay and showing app");
      y();
      // Пробуем еще раз вызвать ensureTelegramBootstrapped
      try {
        ensureTelegramBootstrapped();
      } catch (error) {
        console.error("[INIT ERROR] Error in timeout ensureTelegramBootstrapped:", error);
      }
    }
  }, 15000);
  
  // Экспортируем утилиты для использования в других модулях
  window.__domCacheManager = domCacheManager;
  window.__loadingIndicatorManager = loadingIndicatorManager;
  window.__userMessageManager = userMessageManager;
  
  // Экспортируем утилиты производительности
  if (typeof window.__performanceUtils === 'undefined') {
    const { debounce, throttle, createBatchedUpdater } = await import('./legacy/utils/performance.js');
    window.__performanceUtils = {
      debounce,
      throttle,
      createBatchedUpdater
    };
  }
}
